import { keywordAdditions, keywordKey, keywordValues, newKeywordStyle } from '../utils/keywordAdditions';
import CampaignIssueTable from './CampaignIssueTable';
import { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faCheckCircle,
  faExclamationTriangle,
  faClock,
  faRocket,
  faUpload,
  faTasks,
  faChartLine,
  faCogs,
  faChevronDown,
  faChevronUp,
  faDownload,
  faMapMarkerAlt,
  faStop,
  faLink,
  faEdit,
  faExternalLinkAlt,
  faLock,
  faRefresh,
  faClipboardCheck
} from '@fortawesome/free-solid-svg-icons';
import ReportingDataVisualization from './ReportingDataVisualization';
import DryRunAssignmentReview from './DryRunAssignmentReview';
import { formatOptimizerCurrency } from '../utils/optimizerResultPresentation';
import { resolveOptimizationSuggestions, resolveOptimizationAnalysisText, blockingIssueRows } from '../utils/optimizationSuggestions';
import { buildResultDownloadName } from '../utils/resultDownloadName';
import {
  isCrimtanOptimizerResult,
  CrimtanCauseSection,
  CrimtanExpansionsSection,
} from './CrimtanOptimizationBreakdown';
import {
  isMmgyOptimizerResult,
  MmgyCauseSection,
  MmgyExpansionsSection,
} from './MmgyOptimizationBreakdown';
import {
  FOCUSED_ANALYSIS_LABELS,
  focusedAnalysisKind,
  focusedAnalysisKindFromMessage,
  FocusedFindingsSection,
  FocusedVerdictSection,
} from './FocusedOptimizerBreakdown';
import FocusedRecommendationsTable, { firstSentence } from './FocusedRecommendationsTable';
import SummaryOptimizerLayout from './SummaryOptimizerLayout';
import {
  optimizeAnalysesEnabledFor,
  ALL_ANALYSES_KIND,
  SUMMARY_KIND,
} from '../utils/optimizeCampaignRun';
import { api, getApiUrl } from '../utils/apiConfig';
import { isResultSectionSharedWithAccount } from '../utils/resultSectionFlag';
import {
  editChangeRows, editSignature, changeSignature, entityLabel, changeText,
  EDIT_STATUS_LABEL, EDIT_STATUS_CLASS, NO_CHANGES,
} from '../utils/editChangeSummary';
import './CampaignActivity.css';

// Where an advisory recommendation gets applied by hand, per destination. Only
// the platforms that actually produce advisory suggestions need an entry; anything
// else falls back to "the platform".
/**
 * Focused analyses whose rows render as a table rather than as cards.
 *
 * All three of them: each row answers the same six questions (which insertion order,
 * which line items, what it is now, what to change it to, why, and can I click it), and
 * a row carries every line item one change applies to instead of the cards' one copy of
 * the same sentence per line. The summary run is not here — it lays out its own page.
 */
const FOCUSED_TABLE_KINDS = new Set(['pacing', 'performance', 'diagnostic']);

/**
 * What a run with no recommendations means, per analysis.
 *
 * "No changes recommended" is ambiguous where it matters most: a pacing run that found
 * every insertion order on track and a pacing run that could not measure one read
 * identically. Each analysis has a different reason for being empty, so each says its
 * own — and the run's own closing verdict follows it when there is one, because that is
 * the sentence the model wrote about this campaign rather than about the general case.
 */
const EMPTY_RUN_REASONS = {
  pacing: 'No pacing changes: every insertion order is projected to land on its goal.',
  performance: 'No performance changes: no line item was found under-scaled or inefficient '
    + 'enough to be worth a change.',
  diagnostic: 'No delivery blockers: every line item is able to serve.',
};

export function emptyRunExplanation(kind, details) {
  if (details?.campaign_readout?.version === 1) {
    return 'No recommendation cards remain after review. See campaign health above for findings and evidence gaps.';
  }
  const verdictKeys = {
    pacing: ['pacing_verdict', 'pacing_summary'],
    performance: ['performance_verdict'],
    diagnostic: ['diagnostic_summary'],
  }[kind] || [];
  const verdict = verdictKeys
    .map((key) => {
      const value = details?.[key];
      return typeof value === 'string' ? value.trim() : null;
    })
    .find(Boolean);
  const reason = EMPTY_RUN_REASONS[kind] || 'No changes recommended.';
  return verdict ? `${reason} ${verdict}` : reason;
}

const ADVISORY_PLATFORM_LABELS = {
  dv360: 'DV360',
  'youtube-dv360': 'DV360',
  ttd: 'The Trade Desk',
  'google-ads-search': 'Google Ads',
  google_search: 'Google Ads',
  search: 'Google Ads',
  'google-ads-pmax': 'Google Ads',
  google_pmax: 'Google Ads',
  'google-ads-demandgen': 'Google Ads',
  google_demandgen: 'Google Ads',
  facebook: 'Meta Ads Manager',
  instagram: 'Meta Ads Manager',
  linkedin: 'LinkedIn Campaign Manager',
  reddit: 'Reddit Ads',
  madhive: 'MadHive',
};

// Actions that upload a source file worth offering back as a download.
const SOURCE_FILE_ACTIONS = ['assign-creatives', 'upload-creatives', 'create-geofence-adgroups'];

const HIGH_OPERATION_COUNT_WARNING = /^High operation count:/i;

export const visibleActivityWarnings = (warnings, hideHighOperationCountWarnings = false) => {
  if (!hideHighOperationCountWarnings) return warnings || [];
  return (warnings || []).filter(warning =>
    !HIGH_OPERATION_COUNT_WARNING.test(String(warning || '').trim())
  );
};

// The source files this message can actually offer for download. Returns [] when
// the action doesn't carry one, the metadata won't parse, or no entry has a
// gcsPath — so callers can gate on length instead of guessing from the raw field.
const getDownloadableSourceFiles = (message) => {
  const action = message.action || message.message_type;
  if (!SOURCE_FILE_ACTIONS.includes(action)) return [];
  let sfMeta = message.source_file_metadata;
  if (typeof sfMeta === 'string') {
    try { sfMeta = JSON.parse(sfMeta); } catch (e) { return []; }
  }
  if (!Array.isArray(sfMeta)) sfMeta = sfMeta ? [sfMeta] : [];
  return sfMeta.filter(f => f?.gcsPath);
};

const sourceMetadataOf = (message) => {
  let metadata = message?.source_file_metadata;
  if (typeof metadata === 'string') {
    try { metadata = JSON.parse(metadata); } catch (_) { return metadata; }
  }
  if (Array.isArray(metadata)) metadata = metadata[0];
  return metadata && (typeof metadata === 'object' || typeof metadata === 'string') ? metadata : null;
};

// Before Apply Change batches got their own source marker, they used the same
// `clw_batch_edit` marker as campaign-grid saves. optimizeMessageId is the
// deterministic discriminator: CLW batch saves do not reference an optimizer
// result, while an Apply Change always does.
const isApplyChangeMetadata = (metadata) => metadata?.source === 'apply_actionable_suggestion'
  || (metadata?.source === 'clw_batch_edit' && Boolean(metadata?.optimizeMessageId));

const isEditCampaignMessage = (message) => (message?.action || message?.message_type) === 'edit-campaign';

const successfulEdit = (message) => {
  if ((message?.action || message?.message_type) !== 'edit-campaign'
    || message?.status !== 'completed') return false;
  const result = message?.json_response?.api_result || message?.json_response;
  if (result?.success === false || result?.partial === true || Number(result?.failed_count || 0) > 0) return false;
  return !Array.isArray(result?.results) || result.results.every((item) => item?.success !== false);
};

// Before changeGroupId was stamped into activity metadata, the structured
// batch and campaign_change_log still carried the complete mutation. Accept a
// legacy group only when its exact signature set and cardinality are unique.
const legacyChangeGroupId = (message, changes) => {
  const metadata = sourceMetadataOf(message);
  const edits = metadata?.config_payload?.batch?.edits;
  if (!Array.isArray(edits) || edits.length === 0) return null;
  const wanted = edits.map(editSignature).sort();
  const groups = new Map();
  for (const change of changes || []) {
    if (!change.changeGroupId) continue;
    if (!groups.has(change.changeGroupId)) groups.set(change.changeGroupId, []);
    groups.get(change.changeGroupId).push(change);
  }
  const matches = [...groups.entries()].filter(([, rows]) => {
    const actual = rows.map(changeSignature).sort();
    return actual.length === wanted.length && actual.every((signature, index) => signature === wanted[index]);
  });
  return matches.length === 1 ? matches[0][0] : null;
};

/**
 * The before/after value on an optimizer suggestion, rendered so a list reads as a list.
 *
 * Targeting expansions carry their actual values — nine domains, fifty keywords, a set of
 * audience ids. Joining those into one comma-separated line inside an inline <code> made
 * the thing the trader most needs to see the thing hardest to read, and when the value
 * arrived as prose ("expand with additional endemic travel publishers") the real list was
 * only visible by opening the raw JSON.
 *
 * `apply.value` wins when present: it is the validated, machine-readable list that Apply
 * will actually send, so it cannot disagree with what the button does.
 */
const SuggestionValue = ({ value, applyValue, additions, tone = 'current' }) => {
  const palette = tone === 'recommended'
    ? { bg: '#e8f5e9', fg: '#2e7d32', chip: '#c8e6c9' }
    : { bg: '#f5f5f5', fg: '#333', chip: '#e0e0e0' };

  const asItems = (v) => {
    if (Array.isArray(v)) {
      return v.map((x) => (typeof x === 'object' && x !== null
        ? (x.keyword || x.value || x.name || JSON.stringify(x))
        : String(x)));
    }
    return null;
  };

  const items = additions ? keywordValues(applyValue ?? value) : asItems(applyValue) || asItems(value);

  if (!items) {
    const text = (typeof value === 'object' && value !== null)
      ? JSON.stringify(value) : (value ?? '—');
    return (
      <code style={{ backgroundColor: palette.bg, padding: '4px 8px', borderRadius: '3px',
        fontSize: '0.9em', color: palette.fg, display: 'inline-block',
        fontWeight: tone === 'recommended' ? 600 : 400 }}>{String(text)}</code>
    );
  }

  return (
    <div>
      <div style={{ fontSize: '0.75em', color: '#666', marginBottom: '4px' }}>
        {items.length} {items.length === 1 ? 'value' : 'values'}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px',
        maxHeight: '160px', overflowY: 'auto' }}>
        {items.map((item, i) => (
          <span key={`${item}-${i}`} style={{ backgroundColor: palette.chip,
            color: palette.fg, padding: '2px 6px', borderRadius: '3px',
            fontSize: '0.8em', fontFamily: 'monospace', wordBreak: 'break-all',
            ...(additions?.has(keywordKey(item)) ? newKeywordStyle : {}) }}>{item}{additions?.has(keywordKey(item)) && <span> · New</span>}</span>
        ))}
      </div>
    </div>
  );
};

const CampaignActivity = ({ messages, isLoading, onApplyChanges, applyingChanges, campaign, platformCampaignId, isSuggestionApplied, onStopMessage, assigningMessageId, accountId, userId, onOptimizeCampaign, onRefreshCampaign, onEditCampaign, isOptimizing, isRefreshing, accountSettings, onUploadCreatives, canDownloadResultZip, superAdminUserId, canViewRawJson = false, onActivityChanged = null, hideHighOperationCountWarnings = false,
  /* Measured effect of the changes already applied, keyed exactly the way the
     Apply button keys its rows: appliedImpacts[optimizeMessageId][suggestionIndex].
     Absent ⇒ the recommendations table renders no Actual Impact column at all,
     rather than a column of dashes. */
  appliedImpacts = null }) => {
  const [expandedMessages, setExpandedMessages] = useState(new Set());
  // { [messageId]: { summary: string, loading: boolean } }
  const [llmSummaries, setLlmSummaries] = useState({});
  // { [messageId]: 'idle' | 'loading' | 'queued' | 'error' }
  const [sidekickFixerState, setSidekickFixerState] = useState({});
  // The Run Summary action currently being applied, by action_id. The page's Apply
  // buttons live inside injected HTML and cannot hold React state of their own, so the
  // in-flight one is tracked here and reflected back into the DOM by
  // SummaryOptimizerLayout.
  const [summaryApplyingId, setSummaryApplyingId] = useState(null);
  const [changeLog, setChangeLog] = useState([]);
  const [undoingGroup, setUndoingGroup] = useState(null);

  // Messages whose change-log rows we need: Apply Change batches (the group
  // Undo button) and any tracked edit (changeGroupId), so the Changes table can
  // mark rows undone since — from the CLW change log or that button.
  const changeLogKey = (messages || [])
    .filter((message) => {
      const metadata = sourceMetadataOf(message);
      return isApplyChangeMetadata(metadata)
        || (isEditCampaignMessage(message) && Boolean(metadata?.changeGroupId));
    })
    .map((message) => message.message_id)
    .filter(Boolean)
    .sort()
    .join(',');

  // Change rows are the authority for whether an Apply Change batch is still
  // undoable. The activity metadata supplies the exact group id; the log tells
  // us whether every inverse still exists or the batch was already undone.
  useEffect(() => {
    if (!campaign?.id || !changeLogKey) {
      setChangeLog([]);
      return undefined;
    }
    let cancelled = false;
    api.get(getApiUrl(`/api/v2/campaigns/${campaign.id}/changelog`), {
      params: { accountId, userId },
    }).then((res) => {
      if (!cancelled) setChangeLog(Array.isArray(res.data?.changes) ? res.data.changes : []);
    }).catch(() => {
      if (!cancelled) setChangeLog([]);
    });
    return () => { cancelled = true; };
  }, [campaign?.id, accountId, userId, changeLogKey]);

  const undoApplyChange = async (event, groupId) => {
    event.stopPropagation();
    if (undoingGroup) return;
    if (!window.confirm('Undo every successful edit from this Apply Change?\n\nThis sends the exact inverse changes to the live campaign.')) return;
    setUndoingGroup(groupId);
    try {
      const res = await api.post(
        getApiUrl(`/api/v2/campaigns/${campaign.id}/changelog/groups/${groupId}/undo`),
        { accountId, userId },
      );
      if (Array.isArray(res.data?.changes)) setChangeLog(res.data.changes);
      if (onActivityChanged) await onActivityChanged();
    } catch (error) {
      alert(error?.response?.data?.error || error.message || 'Failed to undo Apply Change');
    } finally {
      setUndoingGroup(null);
    }
  };

  // Auto-expand the top (most recent) activity card so users see the latest
  // action's details without having to click to expand. We only auto-expand
  // when the top message ID changes — if the user manually collapses the top
  // card, it stays collapsed until a NEW activity bumps a new message to top.
  const topMessageId = messages && messages.length > 0 ? messages[0].message_id : null;
  const prevTopMessageIdRef = useRef(null);
  useEffect(() => {
    if (topMessageId && topMessageId !== prevTopMessageIdRef.current) {
      setExpandedMessages((prev) => {
        if (prev.has(topMessageId)) return prev; // already expanded, no-op
        const next = new Set(prev);
        next.add(topMessageId);
        return next;
      });
      prevTopMessageIdRef.current = topMessageId;
    }
  }, [topMessageId]);

  const isAiErrorSummaryEnabled = accountSettings?.['ai-error-summary'] === 'true';

  // Whether this account opted every role into the raw Result JSON. The parent
  // folds this into canViewRawJson; it is read again here only to keep the
  // "Super admin only" notice from claiming exclusivity that no longer holds.
  const isResultSectionShared = isResultSectionSharedWithAccount(accountSettings);

  // Helper to check if a feature is enabled for a given platform
  const isFeatureEnabledForPlatform = (featureKey, platform) => {
    if (!accountSettings || !platform) return false;

    const featureSetting = accountSettings[featureKey];
    if (!featureSetting) return false;

    // Parse comma-separated list of platforms
    const enabledPlatforms = featureSetting
      .split(',')
      .map(p => p.trim().toLowerCase())
      .filter(p => p.length > 0);

    // Normalize platform names
    let normalizedPlatform = platform.toLowerCase();
    if (normalizedPlatform === 'google_search' || normalizedPlatform === 'google-search' ||
        normalizedPlatform === 'g-ads-search' || normalizedPlatform === 'search') {
      normalizedPlatform = 'google-ads-search';
    } else if (normalizedPlatform === 'fb') {
      normalizedPlatform = 'facebook';
    }

    return enabledPlatforms.includes(normalizedPlatform);
  };

  const toggleMessageExpansion = (messageId) => {
    const newExpanded = new Set(expandedMessages);
    if (newExpanded.has(messageId)) {
      newExpanded.delete(messageId);
    } else {
      newExpanded.add(messageId);
    }
    setExpandedMessages(newExpanded);
  };

  const getMessageIcon = (messageType, status) => {
    const iconMap = {
      'orchestration': faRocket,
      'fast-track': faRocket,
      'fast-track-shell': faRocket,
      'salesforce-orchestration': faRocket,
      'upload-creatives': faUpload,
      'assign-creatives': faTasks,
      'analyze-campaign': faChartLine,
      'optimize-campaign': faChartLine,
      'campaign-audit': faClipboardCheck,
      'campaign-optimizer': faCogs,
      'edit-campaign': faCogs,
      'gt-api': faMapMarkerAlt,
      'import-campaign': faDownload,
      'auto-pacing': faClock,
      'refresh-campaign': faRefresh,
      'create-geofence-adgroups': faMapMarkerAlt,
      'rate-limit-retry': faClock
    };

    const baseIcon = iconMap[messageType] || faClock;

    // Return status-specific styling
    if (status === 'processing') {
      return <FontAwesomeIcon icon={faSpinner} spin className="message-icon processing" />;
    } else if (status === 'completed') {
      return <FontAwesomeIcon icon={faCheckCircle} className="message-icon completed" />;
    } else if (status === 'failed' || status === 'error') {
      return <FontAwesomeIcon icon={faExclamationTriangle} className="message-icon failed" />;
    } else if (status === 'stopped') {
      return <FontAwesomeIcon icon={faStop} className="message-icon stopped" />;
    } else {
      return <FontAwesomeIcon icon={baseIcon} className="message-icon pending" />;
    }
  };

  const getMessageTypeLabel = (messageType) => {
    const labelMap = {
      'orchestration': 'Campaign Orchestration',
      'fast-track': 'Fast Track',
      'fast-track-shell': 'Fast Track Shell',
      'salesforce-orchestration': 'Salesforce Orchestration',
      'upload-creatives': 'Upload Creatives',
      'assign-creatives': 'Assign Creatives',
      'analyze-campaign': 'Campaign Analysis',
      'optimize-campaign': 'Optimize Campaign',
      'campaign-audit': 'Campaign Audit',
      'campaign-optimizer': 'Campaign Optimization',
      'edit-campaign': 'Edit Campaign',
      'gt-api': 'GroundTruth',
      'import-campaign': 'Import Campaign',
      'campaign-copy': 'Copy Campaign',
      'auto-pacing': 'Auto Pacing',
      'refresh-campaign': 'Refresh Campaign',
      'create-geofence-adgroups': 'Create Geofence Ad Groups',
      'rate-limit-retry': 'Rate-Limit Retry'
    };

    return labelMap[messageType] || messageType;
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      'processing': 'In Progress',
      'completed': 'Completed',
      'failed': 'Failed',
      'error': 'Error',
      'pending': 'Pending',
      'stopped': 'Stopped',
      'timeout': 'Timed Out'
    };

    return statusMap[status] || status;
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const appliedLabel = (application) => (
    application?.appliedAt
      ? `Change Applied on ${formatTimestamp(application.appliedAt)}`
      : 'Change Applied'
  );

  const truncateContent = (content, maxLength = 100) => {
    if (!content || content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  // Function to reorganize JSON for gt-api responses
  const reorganizeGtApiJson = (jsonResponse) => {
    if (!jsonResponse) return jsonResponse;

    // Create a new object with the desired order
    const reorganized = {};

    // Add top-level fields first
    if ('success' in jsonResponse) reorganized.success = jsonResponse.success;
    if ('input_summary' in jsonResponse) reorganized.input_summary = jsonResponse.input_summary;
    if ('errors' in jsonResponse) reorganized.errors = jsonResponse.errors;
    if ('warnings' in jsonResponse) reorganized.warnings = jsonResponse.warnings;

    // Add all other fields
    Object.keys(jsonResponse).forEach(key => {
      if (!['success', 'input_summary', 'errors', 'warnings'].includes(key)) {
        reorganized[key] = jsonResponse[key];
      }
    });

    return reorganized;
  };



  // Extract errors and warnings from message response for summary display
  const getErrorsAndWarnings = (message) => {
    const apiResult = message.json_response?.api_result || message.json_response;
    if (!apiResult) return { errors: [], warnings: [], infos: [] };

    // Ensure items are strings (backend may return objects with {error, campaign_id})
    const toStringItem = (item) => {
      if (typeof item === 'string') return item;
      if (item && typeof item === 'object') return item.error || item.message || JSON.stringify(item);
      return String(item);
    };

    const errors = [];
    const warnings = [];
    // A launcher's third channel: findings that are true but ask nothing of
    // the operator — an optional field left unset, a pool seeded from the
    // brief, a hygiene drop. Keeping them off `warnings` is what stops them
    // crowding out the ones that need a decision before launch. Launchers that
    // do not send `infos` leave this empty and render exactly as before.
    const infos = [];

    // Top-level errors and warnings
    if (Array.isArray(apiResult.errors)) {
      errors.push(...apiResult.errors.map(toStringItem));
    }
    if (Array.isArray(apiResult.warnings)) {
      warnings.push(...apiResult.warnings.map(toStringItem));
    }
    if (Array.isArray(apiResult.infos)) {
      infos.push(...apiResult.infos.map(toStringItem));
    }

    // Validation errors & warnings (fast-track-shell)
    const validation = apiResult.details?.validation;
    if (validation) {
      if (Array.isArray(validation.errors)) errors.push(...validation.errors.map(toStringItem));
      if (Array.isArray(validation.warnings)) warnings.push(...validation.warnings.map(toStringItem));
      if (Array.isArray(validation.infos)) infos.push(...validation.infos.map(toStringItem));
    }

    // Unmatched creatives (assign-creatives)
    const unmatched = apiResult.details?.assignments?.unmatched_creatives;
    if (Array.isArray(unmatched) && unmatched.length > 0) {
      unmatched.forEach(item => {
        warnings.push(`Unmatched: ${item.asset_id || item.placement_id || 'unknown'} — ${item.reason}`);
      });
    }

    return {
      errors,
      warnings: visibleActivityWarnings(warnings, hideHighOperationCountWarnings),
      infos
    };
  };

  // Call LLM to summarize a message — uses full JSON for failed messages, errors/warnings extraction otherwise
  const summarizeMessage = (message) => {
    const isFailed = message.status === 'failed';
    const body = isFailed
      ? { fullResponse: message.json_response || { action: message.action, content: message.content }, userId, messageId: message.message_id }
      : (() => {
          const { errors, warnings, infos } = getErrorsAndWarnings(message);
          if (errors.length === 0 && warnings.length === 0 && infos.length === 0) return null;
          return { errors, warnings, infos, userId, messageId: message.message_id };
        })();

    if (!body) return;

    setLlmSummaries(prev => ({ ...prev, [message.message_id]: { loading: true, previous: prev[message.message_id] || message.llm_summary } }));

    api.post(getApiUrl('/api/v2/ai/summarize-errors'), body)
      .then(response => {
        setLlmSummaries(prev => ({
          ...prev,
          [message.message_id]: { ...response.data, loading: false }
        }));
      })
      .catch(() => {
        setLlmSummaries(prev => ({ ...prev, [message.message_id]: { ...(prev[message.message_id]?.previous || message.llm_summary), loading: false, error: true } }));
      });
  };

  // Download a GCS result folder as a zip (super-admin, or account admin when
  // the account has the result-zip download flag enabled)
  const downloadGcsResultZip = async (e, gcsPath, label) => {
    e.stopPropagation();
    try {
      // Use superAdminUserId so the backend can verify super-admin status even
      // when the session is an impersonated one (impersonated user's ID wouldn't pass the check).
      const response = await api.get(getApiUrl('/api/v2/admin/gcs-result-zip'), {
        params: { gcsPath, accountId, userId: superAdminUserId || userId },
        responseType: 'blob'
      });
      const folderName = (campaign?.folder_name || '').replace(/[/\\?%*:|"<>]/g, '-').trim();
      const campaignName = (campaign?.name || '').replace(/[/\\?%*:|"<>]/g, '-').trim();
      const parts = [folderName, campaignName, label].filter(Boolean);
      const zipName = parts.join(' - ');
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${zipName}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('GCS zip download failed:', err);
      alert('Failed to download result. Check console for details.');
    }
  };

  // Auto-summarize any completed message that has errors/warnings
  useEffect(() => {
    if (!isAiErrorSummaryEnabled) return;
    if (!messages || messages.length === 0) return;
    messages.forEach(message => {
      if (message.status === 'processing') return;
      if (message.llm_summary) return; // already persisted
      if (llmSummaries[message.message_id] !== undefined) return;

      if (message.status === 'failed') {
        // Summarize failed messages even when json_response is absent — fall back to content
        if (!message.json_response && !message.content) return;
        summarizeMessage(message);
        return;
      }

      if (!message.json_response) return;
      const { errors, warnings } = getErrorsAndWarnings(message);
      if (errors.length === 0 && warnings.length === 0) return;

      summarizeMessage(message);
    });
  }, [messages, isAiErrorSummaryEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // Function to highlight errors in JSON and make URLs clickable
  const highlightErrors = (jsonString) => {
    const lines = jsonString.split('\n');
    return lines.map((line, index) => {
      // Check if line contains a URL field
      const urlMatch = line.match(/"url":\s*"([^"]+)"/);
      if (urlMatch) {
        const url = urlMatch[1];
        const beforeUrl = line.substring(0, urlMatch.index);
        const afterUrl = line.substring(urlMatch.index + urlMatch[0].length);

        return (
          <div key={index}>
            {beforeUrl}
            <span>"url": "</span>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#3b82f6', textDecoration: 'underline' }}
              onClick={(e) => e.stopPropagation()}
            >
              {url}
            </a>
            <span>"</span>
            {afterUrl}
          </div>
        );
      }

      // Skip highlighting if error value is null or empty array
      const isNullError = /"error":\s*null/i.test(line) || /"errors":\s*null/i.test(line);
      const isEmptyErrorArray = /"errors":\s*\[\s*\]/i.test(line);

      if (isNullError || isEmptyErrorArray) {
        return <div key={index}>{line}</div>;
      }

      // Check if line contains error-related keys or values
      const hasErrorKey = /"error":|"errors":/i.test(line);
      const hasErrorValue = /"error":|"failed":|"ERROR":|"FAILED":/i.test(line);
      const isErrorArray = /"errors":\s*\[/i.test(line);

      // Check if this is an error message value (string containing error text)
      const isErrorMessage = /:\s*".*(?:error|failed|ERROR|FAILED).*"/i.test(line) && !/"success":/i.test(line);

      if (hasErrorKey || hasErrorValue || isErrorArray || isErrorMessage) {
        return (
          <div key={index} style={{ color: '#ff4444', fontWeight: 'bold' }}>
            {line}
          </div>
        );
      }
      return <div key={index}>{line}</div>;
    });
  };

  // Function to download JSON
  const downloadJson = (jsonData, messageId, message = null) => {
    const jsonString = JSON.stringify(jsonData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    // Named for what it is — campaign, action, reporting window, run date —
    // instead of two concatenated uuids (see utils/resultDownloadName).
    link.download = buildResultDownloadName({
      campaignName: campaign?.name,
      action: message?.action || message?.message_type,
      jsonResponse: jsonData,
      timestamp: message?.message_timestamp || message?.timestamp,
      fallbackId: messageId,
    });
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Function to download SDF file from GCS via backend API
  const downloadSdfFile = async (gcsPath, filename) => {
    try {
      console.log('Downloading SDF file:', { gcsPath, filename, campaignId: campaign.id, accountId, userId });

      // Get signed URL from backend using the configured API instance
      const params = {};
      if (accountId) params.accountId = accountId;
      if (userId) params.userId = userId;

      // Add GCS path and filename as query parameters
      params.gcsPath = gcsPath;
      params.filename = filename;

      const response = await api.get(getApiUrl(`/api/v2/campaigns/${campaign.id}/sdf-files/download`), {
        params
      });

      console.log('Response received:', response.data);

      if (response.data && response.data.publicUrl) {
        // Open download in new tab/window
        window.open(response.data.publicUrl, '_blank');
      } else {
        console.error('No download URL received for file:', filename);
        alert('Failed to get download URL for SDF file');
      }
    } catch (error) {
      console.error('Error downloading SDF file:', error);
      alert(`Failed to download SDF file: ${error.response?.data?.error || error.message}`);
    }
  };

  const downloadSourceFile = async (gcsPath) => {
    try {
      const params = {};
      if (accountId) params.accountId = accountId;
      if (userId) params.userId = userId;
      params.gcsPath = gcsPath;
      params.filename = gcsPath.split('/').pop();

      const response = await api.get(getApiUrl(`/api/v2/campaigns/${campaign.id}/source-files/download`), {
        params
      });

      if (response.data && response.data.publicUrl) {
        window.open(response.data.publicUrl, '_blank');
      } else {
        console.error('No download URL received for source file');
        alert('Failed to get download URL for source file');
      }
    } catch (error) {
      console.error('Error downloading source file:', error);
      alert(`Failed to download source file: ${error.response?.data?.error || error.message}`);
    }
  };

  if (isLoading) {
    return (
      <></>
    );
  }

  if (!messages || messages.length === 0) {
    return (
      <div className="campaign-activity empty">
        <h3>Activity</h3>
        <p className="no-activity">No activity found for this campaign.</p>
      </div>
    );
  }

  return (
    <div className="campaign-activity">
      <h3>Activity ({messages.length})</h3>
      <div className="activity-timeline">
        {messages.map((message) => {
          const isExpanded = expandedMessages.has(message.message_id);
          const hasResult = message.json_response && Object.keys(message.json_response).length > 0;

          // The raw response body and its Download JSON button are restricted:
          // super admins always, everyone else only where the account turned on
          // `enable-result-section`. Whoever is left out still keeps the
          // READABLE parts of this section — the errors/warnings lists, the
          // plain-language summary, and the artifact downloads (SDF, Ad SDF,
          // source files) — which is why the whole section isn't simply hidden.
          // (The result-zip buttons are NOT here; they render in the collapsed
          // row above, so canDownloadResultZip must not gate this section.)
          //
          // But with the JSON gone, a plain successful message would leave an
          // empty "Result" heading, which is most messages. So the section
          // renders only when something is actually left to show.
          //
          // These gates mirror each button's own render condition exactly. A
          // looser approximation (e.g. "has source_file_metadata") turns the
          // heading back on for messages whose buttons then decline to render.
          const { errors: resultErrors, warnings: resultWarnings } = getErrorsAndWarnings(message);
          // The SDF buttons accept the name in EITHER field; the source-file
          // button reads the single `action || message_type` value. Keep both
          // shapes as they are — a mismatch here hides a working download.
          const isAction = (name) => message.action === name || message.message_type === name;
          const downloadableSourceFiles = getDownloadableSourceFiles(message);
          const hasArtifactDownloads = Boolean(
            (isAction('edit-campaign')
              && campaign?.destination === 'youtube-dv360'
              && message.json_response?.uploaded_sdf_files?.length > 0)
            || (isAction('assign-creatives') && message.json_response?.ad_sdf_gcs_path)
            || downloadableSourceFiles.length > 0
          );
          // Everything that reports on the outcome: the raw JSON, the error and
          // warning lists, and the AI summary of a failure (whose Summarize
          // button sits in the heading row but is still about the result). The
          // artifact downloads are deliberately excluded — see below.
          const hasResultBody = Boolean(
            canViewRawJson
            || resultErrors.length > 0
            || resultWarnings.length > 0
            || (isAiErrorSummaryEnabled && message.status === 'failed')
          );
          const showResultSection = hasResult && (hasResultBody || hasArtifactDownloads);
          // Name the section for what it actually holds.
          const resultSectionTitle = hasResultBody ? 'Result' : 'Files';

          // Check if api_result.success is false to show simplified view with processing ID
          const apiResult = message.json_response?.api_result || message.json_response;

          // Check if this is a message type that has success field in response
          const MESSAGE_TYPES_WITH_SUCCESS_FIELD = [
            'fast-track', 'fast-track-shell', 'salesforce-orchestration', 'assign-creatives',
            'import-campaign', 'upload-creatives', 'gt-api'
          ];
          const hasSuccessField = MESSAGE_TYPES_WITH_SUCCESS_FIELD.includes(message.message_type) ||
                                  MESSAGE_TYPES_WITH_SUCCESS_FIELD.includes(message.action);

          // Check if triggered by auto pacing, manual pacing, or bulk auto pacing (metadata stored in source message's file_metadata)
          // The metadata is stored on the user message, which is linked via source_message_id
          const sourceFileMetadata = sourceMetadataOf(message);
          const isAutoPacing = sourceFileMetadata?.source === 'auto_pacing' ||
                               (typeof sourceFileMetadata === 'string' && sourceFileMetadata.includes('auto_pacing'));
          const isManualPacing = sourceFileMetadata?.source === 'manual_pacing' ||
                                 sourceFileMetadata?.triggerType === 'manual_pacing';
          const isBulkAutoPacing = sourceFileMetadata?.source === 'bulk_auto_pacing' ||
                                   sourceFileMetadata?.triggerType === 'bulk_auto_pacing' ||
                                   sourceFileMetadata?.source === 'bulk_auto_pacing_scheduler' ||
                                   (typeof sourceFileMetadata === 'string' && sourceFileMetadata.includes('bulk_auto_pacing'));
          const autoPacingTriggerType = sourceFileMetadata?.triggerType; // 'scheduled', 'manual', 'manual_pacing', or 'bulk_auto_pacing'

          // Check if triggered by creative scheduling
          const isCreativeScheduling = sourceFileMetadata?.source === 'creative_scheduling' ||
                                       (typeof sourceFileMetadata === 'string' && sourceFileMetadata.includes('creative_scheduling'));
          const creativeSchedulingTriggerType = sourceFileMetadata?.triggerType; // 'scheduler' or 'immediate'
          const creativeSchedulingAction = sourceFileMetadata?.action; // 'enable' or 'disable'

          // Check if triggered by ad group scheduling
          const isAdGroupScheduling = sourceFileMetadata?.source === 'adgroup_scheduling' ||
                                      (typeof sourceFileMetadata === 'string' && sourceFileMetadata.includes('adgroup_scheduling'));
          const adGroupSchedulingTriggerType = sourceFileMetadata?.triggerType; // 'scheduler' or 'immediate'
          const adGroupSchedulingAction = sourceFileMetadata?.action; // 'enable' or 'disable'

          // Check if this was the nightly performance analysis rather than
          // someone pressing "Run Full Analysis". The scheduler has to send a
          // real userId — POST /:id/optimize requires one — so it borrows the
          // account's first admin, and without this the row read "by <that
          // person's name>" for a run nobody asked for.
          const isPerformanceSchedule = sourceFileMetadata?.source === 'performance_analysis_scheduler';

          // A Frequence file drop, not a person. frequenceFileScheduler polls
          // every minute and dispatches campaign creation under the partner
          // config's created_by admin — or, when that row has none, a hardcoded
          // user id. It has ALWAYS sent source:'frequence'
          // (frequenceFileScheduler.js:3781, spread last into the chat metadata
          // at routes/v2/campaigns.js:741); this ladder just never had a branch
          // for it, so a marker that arrived correctly fell through to the
          // borrowed human's name.
          const isFrequence = sourceFileMetadata?.source === 'frequence';

          // Check if triggered by creative manager
          const isCreativeManager = sourceFileMetadata?.source === 'creative_manager' ||
                                    (typeof sourceFileMetadata === 'string' && sourceFileMetadata.includes('creative_manager'));

          const applyChangeGroupId = isApplyChangeMetadata(sourceFileMetadata)
            ? (sourceFileMetadata.changeGroupId || legacyChangeGroupId(message, changeLog)) : null;
          const applyChangeRows = applyChangeGroupId
            ? changeLog.filter((change) => change.changeGroupId === applyChangeGroupId)
            : [];
          const canUndoApplyChange = successfulEdit(message)
            && applyChangeRows.length > 0
            && applyChangeRows.every((change) => change.status === 'applied' && change.undoable);
          const applyChangeUndone = successfulEdit(message)
            && applyChangeRows.length > 0
            && applyChangeRows.every((change) => change.status === 'reverted');

          // What this edit changed, for every role — the raw JSON is gated.
          const editChanges = isAction('edit-campaign')
            ? editChangeRows({ metadata: sourceFileMetadata, result: message.json_response, changes: changeLog, status: message.status })
            : NO_CHANGES;

          // Determine status class and label
          const isRateLimitRetry = message.message_type === 'rate-limit-retry' || message.action === 'rate-limit-retry';
          let statusClass, statusLabel;
          if (isRateLimitRetry) {
            // Status encodes the notice's meaning (see backend _recordCampaignActivity):
            // pending = parked awaiting the quota window, completed = re-fired,
            // failed = out of attempts. Reuse the standard badge colors.
            statusClass = `status-badge ${message.status}`;
            statusLabel = message.status === 'pending' ? 'Retry Scheduled'
              : message.status === 'completed' ? 'Retried'
                : message.status === 'failed' ? 'Retries Exhausted'
                  : getStatusLabel(message.status);
          } else if (hasSuccessField && apiResult && typeof apiResult.success === 'boolean') {
            statusClass = `status-badge ${apiResult.success ? 'completed' : 'failed'}`;
            statusLabel = apiResult.success ? 'Completed' : 'Failed';
          } else {
            statusClass = `status-badge ${message.status}`;
            statusLabel = getStatusLabel(message.status);
          }

          return (
            <div key={message.message_id} className={`activity-item ${message.is_primary ? 'primary' : ''}`}>
              <div className="activity-header" onClick={() => toggleMessageExpansion(message.message_id)}>
                <div className="activity-icon">
                  {(() => {
                    // For messages with success field that failed, show failed icon
                    if (hasSuccessField && apiResult && apiResult.success === false) {
                      return getMessageIcon(message.message_type, 'failed');
                    }
                    return getMessageIcon(message.message_type, message.status);
                  })()}
                </div>
                <div className="activity-content">
                  <div className="activity-title">
                    {/* A focused optimizer run says which analysis it is — on the result
                        envelope once complete, on sub_action while still running — so the
                        row is never titled the generic "Optimize Campaign". */}
                    <span className="message-type">
                      {FOCUSED_ANALYSIS_LABELS[focusedAnalysisKindFromMessage(message)]
                        || getMessageTypeLabel(message.message_type || message.action)}
                    </span>
                    {message.is_primary && <span className="primary-badge">Primary</span>}
                    <span className={statusClass}>
                      {statusLabel}
                    </span>
                    {(canUndoApplyChange || applyChangeUndone) && (
                      <button
                        type="button"
                        onClick={(event) => canUndoApplyChange && undoApplyChange(event, applyChangeGroupId)}
                        disabled={!canUndoApplyChange || Boolean(undoingGroup)}
                        title={applyChangeUndone
                          ? 'This Apply Change has been undone'
                          : 'Send the exact inverse of this Apply Change to the live campaign'}
                        style={{
                          marginLeft: '8px', padding: '4px 9px', fontSize: '12px',
                          backgroundColor: applyChangeUndone ? '#e9ecef' : '#fff',
                          color: applyChangeUndone ? '#6c757d' : '#b42318',
                          border: '1px solid #d0d5dd', borderRadius: '4px',
                          cursor: canUndoApplyChange && !undoingGroup ? 'pointer' : 'not-allowed',
                        }}
                      >
                        {undoingGroup === applyChangeGroupId ? 'Undoing…' : applyChangeUndone ? 'Undone' : 'Undo'}
                      </button>
                    )}
                    {/* Inline error/warning count badges — visible without expanding */}
                    {message.status !== 'processing' && message.json_response && (() => {
                      const { errors: hdrErrors, warnings: hdrWarnings } = getErrorsAndWarnings(message);
                      if (hdrErrors.length === 0 && hdrWarnings.length === 0) return null;
                      return (
                        <span style={{ marginLeft: '8px', display: 'inline-flex', gap: '4px', alignItems: 'center' }}>
                          {hdrErrors.length > 0 && (
                            <span style={{ fontSize: '11px', padding: '2px 7px', backgroundColor: '#fdd', border: '1px solid #faa', borderRadius: '10px', color: '#c00', fontWeight: '600' }}>
                              {hdrErrors.length} error{hdrErrors.length !== 1 ? 's' : ''}
                            </span>
                          )}
                          {hdrWarnings.length > 0 && (
                            <span style={{ fontSize: '11px', padding: '2px 7px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '10px', color: '#856404', fontWeight: '600' }}>
                              {hdrWarnings.length} warning{hdrWarnings.length !== 1 ? 's' : ''}
                            </span>
                          )}
                        </span>
                      );
                    })()}
                    {/* Stop button for processing single messages */}
                    {message.status === 'processing' &&
                     onStopMessage &&
                     ['upload-creatives', 'assign-creatives', 'analyze-campaign', 'campaign-optimizer', 'optimize-campaign', 'edit-campaign', 'gt-api', 'refresh-campaign', 'create-geofence-adgroups'].includes(message.action) && (
                      <button
                        className="stop-message-btn"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent expanding the message
                          if (window.confirm('Are you sure you want to stop this operation?')) {
                            onStopMessage(message.message_id);
                          }
                        }}
                        title="Stop processing"
                        style={{
                          marginLeft: '8px',
                          padding: '4px 8px',
                          fontSize: '12px',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        <FontAwesomeIcon icon={faStop} />
                        Stop
                      </button>
                    )}
                    {/* Fix it by Sidekick AI — universal for any message with errors/warnings */}
                    {(message.status === 'completed' || message.status === 'failed') &&
                     message.json_response &&
                     (() => {
                       const { errors: fixErrors, warnings: fixWarnings } = getErrorsAndWarnings(message);
                       if (fixErrors.length === 0) return null; // Only show for actual errors, not warnings-only
                       const fixState = sidekickFixerState[message.message_id] || 'idle';
                       return (
                         <button
                           onClick={(e) => {
                             e.stopPropagation();
                             setSidekickFixerState(prev => ({ ...prev, [message.message_id]: 'loading' }));
                             const processingId = message.json_response?.processing_id || message.json_response?.api_result?.processing_id;
                             api.post(getApiUrl('/api/v2/sidekick-fixer/enqueue'), {
                               messageId: message.message_id,
                               campaignId: campaign?.id,
                               accountId: campaign?.account_id,
                               userId,
                               apiType: message.action || message.message_type,
                               processingId,
                               jsonResponse: message.json_response,
                               errors: fixErrors,
                               warnings: fixWarnings,
                             })
                               .then(() => {
                                 setSidekickFixerState(prev => ({ ...prev, [message.message_id]: 'queued' }));
                               })
                               .catch((err) => {
                                 if (err.response?.status === 409) {
                                   // Already queued — treat as success
                                   setSidekickFixerState(prev => ({ ...prev, [message.message_id]: 'queued' }));
                                 } else {
                                   setSidekickFixerState(prev => ({ ...prev, [message.message_id]: 'error' }));
                                   setTimeout(() => {
                                     setSidekickFixerState(prev => ({ ...prev, [message.message_id]: 'idle' }));
                                   }, 3000);
                                 }
                               });
                           }}
                           disabled={fixState === 'loading' || fixState === 'queued'}
                           title={fixState === 'queued' ? 'Already queued for Sidekick AI' : 'Queue this error for Sidekick AI to fix'}
                           style={{
                             marginLeft: '8px',
                             padding: '4px 8px',
                             fontSize: '12px',
                             backgroundColor: fixState === 'queued' ? '#28a745' : fixState === 'error' ? '#dc3545' : '#6f42c1',
                             color: 'white',
                             border: 'none',
                             borderRadius: '4px',
                             cursor: fixState === 'loading' || fixState === 'queued' ? 'default' : 'pointer',
                             display: 'inline-flex',
                             alignItems: 'center',
                             gap: '4px',
                             opacity: fixState === 'loading' ? 0.7 : 1,
                           }}
                         >
                           {fixState === 'loading' && <FontAwesomeIcon icon={faSpinner} spin />}
                           {fixState === 'queued' ? '\u2713 Queued!' : fixState === 'error' ? '\u2717 Failed' : '\u2728 Fix it by Sidekick AI'}
                         </button>
                       );
                     })()}
                    {/* Assign button for completed upload-creatives */}
                    {message.message_type === 'upload-creatives' && 
                     message.status === 'completed' && 
                     (platformCampaignId || campaign?.platform_campaign_id) && (
                      <button
                        className="assign-creatives-btn"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent expanding the message
                          // Call parent handler (will be passed as prop)
                          if (window.onAssignCreatives) {
                            window.onAssignCreatives(message);
                          }
                        }}
                        title="Assign uploaded creatives to campaign"
                        disabled={assigningMessageId === message.message_id}
                        style={{
                          marginLeft: '12px',
                          padding: '6px 12px',
                          backgroundColor: assigningMessageId === message.message_id ? '#6c757d' : '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: assigningMessageId === message.message_id ? 'not-allowed' : 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontSize: '12px',
                          fontWeight: '500',
                          opacity: assigningMessageId === message.message_id ? 0.8 : 1
                        }}
                      >
                        {assigningMessageId === message.message_id ? (
                          <>
                            <FontAwesomeIcon icon={faSpinner} spin />
                            Assigning...
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faLink} />
                            Assign to Campaign
                          </>
                        )}
                      </button>
                    )}
                    {/* GCS result download buttons for fast-track / fast-track-shell.
                        Visible to super-admins, and to account admins when the
                        'enable-campaign-result-zip-download' flag is on. */}
                    {canDownloadResultZip &&
                     ['fast-track', 'fast-track-shell'].includes(message.action || message.message_type) &&
                     message.json_response && (
                      <>
                        {message.json_response.campaign_result && (
                          <button
                            onClick={(e) => downloadGcsResultZip(e, message.json_response.campaign_result, 'campaign-processor')}
                            title="Download campaign-processor result"
                            style={{
                              marginLeft: '8px',
                              padding: '4px 8px',
                              fontSize: '12px',
                              backgroundColor: '#6f42c1',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            <FontAwesomeIcon icon={faDownload} />
                            Campaign Processor
                          </button>
                        )}
                        {message.json_response.launcher_result && (
                          <button
                            onClick={(e) => downloadGcsResultZip(e, message.json_response.launcher_result, 'campaign-launcher')}
                            title="Download campaign-launcher result"
                            style={{
                              marginLeft: '8px',
                              padding: '4px 8px',
                              fontSize: '12px',
                              backgroundColor: '#0d6efd',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            <FontAwesomeIcon icon={faDownload} />
                            Campaign Launcher
                          </button>
                        )}
                      </>
                    )}
                  </div>
                  {/* Hide message content for cleaner display - only show status-based messages.
                      Rate-limit retry notices are the exception: their content IS the
                      information (attempt count, scheduled time, quota error). */}
                  {(message.message_type === 'rate-limit-retry' || message.action === 'rate-limit-retry') && message.content ? (
                    <div className="activity-description">
                      {message.content}
                    </div>
                  ) : (hasSuccessField && apiResult && apiResult.success === false) ||
                   message.json_response?.success === false ||
                   message.status === 'timeout' ? (
                    <div className="activity-description">
                      {hasSuccessField && apiResult && apiResult.success === false
                        ? "Operation failed"
                        : message.json_response?.success === false
                          ? "Operation failed"
                          : "Operation timeout"}
                    </div>
                  ) : null}
                  <div className="activity-meta">
                    <span className="timestamp">{formatTimestamp(message.message_timestamp)}</span>
                    {isBulkAutoPacing ? (
                      <span className="created-by auto-pacing">by Bulk Auto Pacing</span>
                    ) : isManualPacing ? (
                      <span className="created-by auto-pacing">by Manual Pacing</span>
                    ) : isAutoPacing ? (
                      <span className="created-by auto-pacing">
                        by Auto Pacing ({autoPacingTriggerType === 'manual' ? 'Manual' : 'Scheduled'})
                      </span>
                    ) : isCreativeScheduling ? (
                      <span className="created-by creative-scheduling">
                        by Creative Scheduling ({creativeSchedulingTriggerType === 'immediate' ? 'Immediate' : 'Scheduled'}) - {creativeSchedulingAction === 'enable' ? 'Enable' : 'Disable'}
                      </span>
                    ) : isAdGroupScheduling ? (
                      <span className="created-by creative-scheduling">
                        by Ad Group Scheduling ({adGroupSchedulingTriggerType === 'immediate' ? 'Immediate' : 'Scheduled'}) - {adGroupSchedulingAction === 'enable' ? 'Enable' : 'Disable'}
                      </span>
                    ) : isCreativeManager ? (
                      <span className="created-by creative-manager">by Creative Manager</span>
                    ) : isPerformanceSchedule ? (
                      <span className="created-by auto-pacing">by Daily Performance Analysis</span>
                    ) : isFrequence ? (
                      <span className="created-by auto-pacing">by Frequence File Drop</span>
                    ) : message.created_by_name && (
                      <span className="created-by">by {message.created_by_name}</span>
                    )}
                  </div>
                </div>
                <div className="expand-toggle">
                  <FontAwesomeIcon
                    icon={isExpanded ? faChevronUp : faChevronDown}
                    className="expand-icon"
                  />
                </div>
              </div>

              {isExpanded && (
                <div className="activity-details">
                  <div className="detail-section">
                    <h4>Processing ID</h4>
                    <div className="processing-id-display">
                      <span className="monospace">{message.chat_id}_{message.message_id}</span>
                    </div>
                  </div>

                  {/* What this edit changed, for every role. The raw JSON further
                      down is super-admin only, so without this a non-admin saw an
                      Edit Campaign card with nothing but a processing id. */}
                  {(editChanges.rows.length > 0 || editChanges.description) && (
                    <div className="detail-section">
                      <h4>Changes</h4>
                      {editChanges.description && (
                        <div className="activity-description">Requested: {editChanges.description}</div>
                      )}
                      {editChanges.rows.length > 0 && (
                        <div className="edit-changes-scroll">
                          <table className="edit-changes-table">
                            <thead>
                              <tr><th>Entity</th><th>Field</th><th>Change</th><th>Status</th></tr>
                            </thead>
                            <tbody>
                              {editChanges.rows.map((row) => (
                                <tr key={row.key}>
                                  <td>{[entityLabel(row.entityType), row.entityName].filter(Boolean).join(': ') || '—'}</td>
                                  <td>{row.field}</td>
                                  <td className="edit-change-values">{changeText(row)}</td>
                                  <td>
                                    <span className={`status-badge ${EDIT_STATUS_CLASS[row.status]}`}>{EDIT_STATUS_LABEL[row.status]}</span>
                                    {row.error && <div className="edit-change-error">{row.error}</div>}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Interactive review for dry-run assign-creatives. Renders
                      for DV360 (which uses details.assignments.matched) and
                      TTD (which uses details.planned_assignments). The
                      component handles both shapes. The apply-creative-
                      assignments backend route accepts both destinations. */}
                  {(message.action === 'assign-creatives' || message.message_type === 'assign-creatives')
                    && message.json_response?.details?.dry_run === true
                    && (campaign?.destination === 'dv360' || campaign?.destination === 'ttd') && (
                    <div className="detail-section">
                      <DryRunAssignmentReview
                        campaign={campaign}
                        message={message}
                        accountId={accountId}
                        user={{ userId, id: userId }}
                      />
                    </div>
                  )}

                  {/* Extract and display URL if present in json_response */}
                  {hasResult && message.json_response?.url && (
                    <div className="detail-section">
                      <h4>Campaign URL</h4>
                      <div className="url-display">
                        <a
                          href={message.json_response.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="campaign-url-link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {message.json_response.url}
                        </a>
                      </div>
                    </div>
                  )}

                  {/* Render import-campaign results with action buttons */}
                  {hasResult && message.action === 'import-campaign' && apiResult?.success && apiResult?.campaigns?.length > 0 && (() => {
                    // Get platform from the import response
                    const importPlatform = message.json_response?.platform ||
                                          apiResult?.platform ||
                                          campaign?.destination;
                    const isOptimizeEnabled = isFeatureEnabledForPlatform('optimize-campaign', importPlatform);
                    const isRefreshEnabled = isFeatureEnabledForPlatform('refresh-campaign', importPlatform);

                    return (
                    <div className="detail-section">
                      <h4>Imported Campaign</h4>
                      {apiResult.campaigns.map((importedCampaign, idx) => (
                        <div key={idx} className="imported-campaign-item" style={{ marginBottom: '15px' }}>
                          <div style={{ marginBottom: '10px' }}>
                            <strong>{importedCampaign.name}</strong>
                            <div style={{ fontSize: '0.9em', color: '#666', marginTop: '4px' }}>
                              Campaign ID: {importedCampaign.campaign_id}
                              {importedCampaign.currency_code && (
                                <> · Currency: {importedCampaign.currency_code}</>
                              )}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            {/* View Campaign Button */}
                            {importedCampaign.campaign_url && (
                              <a
                                href={importedCampaign.campaign_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-outline"
                                style={{
                                  padding: '8px 16px',
                                  fontSize: '13px',
                                  textDecoration: 'none',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  border: '1px solid #007bff',
                                  color: '#007bff',
                                  borderRadius: '4px',
                                  backgroundColor: 'transparent'
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <FontAwesomeIcon icon={faExternalLinkAlt} />
                                View Campaign
                              </a>
                            )}

                            {/* Edit Campaign Button - always available */}
                            {onEditCampaign && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEditCampaign(importedCampaign.campaign_id);
                                }}
                                className="btn btn-secondary"
                                style={{
                                  padding: '8px 16px',
                                  fontSize: '13px',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  backgroundColor: '#6c757d',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer'
                                }}
                              >
                                <FontAwesomeIcon icon={faEdit} />
                                Edit Campaign
                              </button>
                            )}

                            {/* Optimize Campaign Button - controlled by account settings.
                                On the platforms with focused launchers (DV360, Google Ads
                                Search, Performance Max) it runs the full focused analysis
                                — diagnostic + pacing + performance — and the combined
                                optimizer is replaced there, same as on the Campaign
                                Actions row. */}
                            {onOptimizeCampaign && isOptimizeEnabled && (() => {
                              // Keyed on the IMPORT's platform, like the enablement checks
                              // above — the campaign record's destination can differ.
                              const runsFullAnalysis = optimizeAnalysesEnabledFor(importPlatform);
                              return (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Platforms without the focused launchers keep the
                                  // historical single-arg call.
                                  if (runsFullAnalysis) {
                                    onOptimizeCampaign(importedCampaign.campaign_id, ALL_ANALYSES_KIND);
                                  } else {
                                    onOptimizeCampaign(importedCampaign.campaign_id);
                                  }
                                }}
                                disabled={isOptimizing}
                                className="btn btn-primary"
                                style={{
                                  padding: '8px 16px',
                                  fontSize: '13px',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  backgroundColor: isOptimizing ? '#ccc' : '#007bff',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: isOptimizing ? 'not-allowed' : 'pointer'
                                }}
                              >
                                {isOptimizing ? (
                                  <>
                                    <FontAwesomeIcon icon={faSpinner} spin />
                                    Optimizing...
                                  </>
                                ) : (
                                  <>
                                    <FontAwesomeIcon icon={faChartLine} />
                                    {runsFullAnalysis ? 'Run Full Analysis' : 'Optimize Campaign'}
                                  </>
                                )}
                              </button>
                              );
                            })()}

                            {/* Refresh Campaign Button - controlled by account settings */}
                            {onRefreshCampaign && isRefreshEnabled && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onRefreshCampaign(importedCampaign.campaign_id);
                                }}
                                disabled={isRefreshing}
                                className="btn btn-secondary"
                                style={{
                                  padding: '8px 16px',
                                  fontSize: '13px',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  backgroundColor: isRefreshing ? '#ccc' : '#6c757d',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: isRefreshing ? 'not-allowed' : 'pointer'
                                }}
                              >
                                {isRefreshing ? (
                                  <>
                                    <FontAwesomeIcon icon={faSpinner} spin />
                                    Refreshing...
                                  </>
                                ) : (
                                  <>
                                    <FontAwesomeIcon icon={faRefresh} />
                                    Refresh Campaign
                                  </>
                                )}
                              </button>
                            )}

                            {/* Upload Assets Button - for imported campaigns */}
                            {onUploadCreatives && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onUploadCreatives();
                                }}
                                className="btn btn-secondary"
                                style={{
                                  padding: '8px 16px',
                                  fontSize: '13px',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  backgroundColor: '#28a745',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer'
                                }}
                              >
                                <FontAwesomeIcon icon={faUpload} />
                                Upload Assets
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    );
                  })()}

                  {/* Render optimize-campaign AND campaign-audit results with actionable
                      buttons. The audit emits the same details.analysis.actionable_suggestions
                      shape the optimizer does, so it renders through this path rather than a
                      parallel one that would drift away from it.

                      A LAUNCH now emits that shape too — the fixes for targeting DV360
                      refused while the campaign was being built ("1 contextual keyword
                      rejected: 'Step Afrika!'"), each carrying the validated edit that
                      applies it. That case is gated on the DATA rather than on a third
                      action name: a launch reaches the feed as orchestration, fast-track
                      or fast-track-shell depending on how it was started, and a name list
                      would silently miss whichever one nobody thought of. A result with no
                      suggestions renders exactly as it did before. */}
                  {hasResult && (['optimize-campaign', 'campaign-audit'].includes(message.action)
                      || (message.json_response?.details?.analysis?.actionable_suggestions || []).length > 0)
                    && message.json_response?.details && (
                    <div className="detail-section">
                      <h4>{['optimize-campaign', 'campaign-audit'].includes(message.action)
                        ? 'Optimization Results' : 'Recommended fixes'}</h4>
                      {message.json_response.details.success === false && message.json_response.details.error ? (
                        <div className="error-message" style={{
                          padding: '15px',
                          backgroundColor: '#fee',
                          border: '1px solid #fcc',
                          borderRadius: '4px',
                          marginTop: '10px',
                          display: 'block'
                        }}>
                          <h5 style={{ color: '#c00', marginTop: 0, marginBottom: '15px' }}>❌ Error</h5>
                          {(() => {
                            const rawError = message.json_response.details.error;
                            const errorText = typeof rawError === 'object' ? (rawError.error || rawError.message || JSON.stringify(rawError)) : String(rawError);

                            // Try to extract the main error message from API errors
                            try {
                              const messageMatch = errorText.match(/"message":\s*"([^"]+)"/);
                              if (messageMatch) {
                                const mainMessage = messageMatch[1];
                                const codeMatch = errorText.match(/"code":\s*(\d+)/);
                                const errorCode = codeMatch ? codeMatch[1] : null;

                                return (
                                  <div style={{ display: 'block' }}>
                                    <div style={{ marginBottom: '12px' }}>
                                      <p style={{ margin: 0, fontSize: '0.95em', lineHeight: '1.5' }}>
                                        {mainMessage}
                                      </p>
                                    </div>
                                    {errorCode && (
                                      <div style={{ marginBottom: '12px' }}>
                                        <p style={{ margin: 0, fontSize: '0.85em', color: '#666' }}>
                                          <strong>Error Code:</strong> {errorCode}
                                        </p>
                                      </div>
                                    )}
                                    {message.json_response.details.campaign_id && (
                                      <div style={{ marginBottom: '12px' }}>
                                        <p style={{ margin: 0, fontSize: '0.85em', color: '#666' }}>
                                          <strong>Campaign ID:</strong> {message.json_response.details.campaign_id}
                                        </p>
                                      </div>
                                    )}
                                    <details style={{ marginTop: '12px' }}>
                                      <summary style={{ cursor: 'pointer', color: '#666', fontSize: '0.85em', userSelect: 'none' }}>
                                        Show full error details
                                      </summary>
                                      <pre style={{
                                        marginTop: '10px',
                                        padding: '10px',
                                        backgroundColor: '#f5f5f5',
                                        borderRadius: '4px',
                                        fontSize: '0.8em',
                                        overflow: 'auto',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word'
                                      }}>
                                        {errorText}
                                      </pre>
                                    </details>
                                  </div>
                                );
                              }
                            } catch (e) {
                              // If parsing fails, fall through to default rendering
                            }

                            // Default: show full error text
                            return (
                              <div style={{ display: 'block' }}>
                                <p style={{ margin: '0 0 12px 0', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.9em' }}>
                                  {errorText}
                                </p>
                                {message.json_response.details.campaign_id && (
                                  <p style={{ margin: 0, fontSize: '0.85em', color: '#666' }}>
                                    <strong>Campaign ID:</strong> {message.json_response.details.campaign_id}
                                  </p>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      ) : (() => {
                          // Get analysis data - support multiple formats:
                          // 1. Google Ads Search: details.analysis.actionable_suggestions
                          // 2. MadHive/Reddit/Facebook: details.actionable_items (with details.analysis as string)
                          const details = message.json_response.details;
                          const analysisData = details.analysis;

                          // Account billing currency (ISO 4217, e.g. EUR/GBP) resolved by the
                          // optimizer backend from customer.currency_code. Money in the
                          // suggestion cards must render in this currency, not a hardcoded "$".
                          // Null → formatOptimizerCurrency falls back to legacy "$" rendering.
                          const currencyCode = message.json_response?.analysis_data?.campaign_structure?.currency_code || null;

                          // Client-specific layout. `client_name` is echoed onto the result by
                          // the optimizer launcher with the slug that actually steered the run,
                          // so it doubles as proof that client's prompt really ran — the account
                          // name never reaches this screen, and an account check alone could not
                          // tell a steered result from a generic one.
                          //
                          // Resolved to a {Cause, Expansions} pair rather than a chain of
                          // per-client booleans: every one of the branches below asks "is this
                          // ANY client layout?", and with a boolean each those branches all have
                          // to be edited again for the next client — which is precisely how MMGY
                          // results would have kept rendering the generic view.
                          const clientBreakdown = (
                            isCrimtanOptimizerResult(message.json_response)
                              ? { Cause: CrimtanCauseSection, Expansions: CrimtanExpansionsSection }
                              : isMmgyOptimizerResult(message.json_response)
                                ? { Cause: MmgyCauseSection, Expansions: MmgyExpansionsSection }
                                : null
                          );
                          const isClientResult = clientBreakdown !== null;

                          // A focused Run Diagnostic / Pacing / Performance result. Its
                          // findings sections replace the client cause/expansion sections
                          // (the envelope still carries client_name, so both would match).
                          const focusedKind = focusedAnalysisKind(message.json_response);

                          // A Run Summary result laid out its OWN page — it already merged
                          // the three analyses, ranked them and rendered the numbers, so
                          // every generic section below would restate what the page states
                          // better. Returned early rather than woven in: this result carries
                          // `recommendations` AND `client_name`, so it would otherwise match
                          // both the client layout and the card list and print each finding
                          // three times.
                          if (focusedKind === SUMMARY_KIND && typeof details?.layout_html === 'string') {
                            const summaryActions = Array.isArray(details.actions) ? details.actions : [];
                            return (
                              <div className="optimization-suggestions">
                                <SummaryOptimizerLayout
                                  html={details.layout_html}
                                  actions={summaryActions}
                                  applyingActionId={summaryApplyingId}
                                  isApplied={onApplyChanges && isSuggestionApplied
                                    ? (index) => isSuggestionApplied(message.message_id, index)
                                    : undefined}
                                  onApply={onApplyChanges
                                    ? (action, index, actionId) => {
                                      setSummaryApplyingId(actionId);
                                      // The apply path is the cards' own: the summary's
                                      // `recommendations` IS `actions`, so `index` is the
                                      // backend suggestion position applied-state is keyed on.
                                      Promise.resolve(onApplyChanges(message, action, index))
                                        .finally(() => setSummaryApplyingId(null));
                                    }
                                    : undefined}
                                />
                              </div>
                            );
                          }

                          // Every platform's own key, resolved in one place — DV360's
                          // pacing_recommendations / recommendations were not read at
                          // all, so a DV360 run rendered "No optimization suggestions
                          // available." over a result full of them.
                          const rawActionableItems = resolveOptimizationSuggestions(details);
                          // Surface the highest-value work first: sort by the composite
                          // priority_score (desc) when the optimizer emitted scoring. Stable
                          // for items without a score (they keep their original relative order
                          // and sink below scored ones). Non-scored platforms are unaffected.
                          // Carry __originalIndex so apply/applied-state stay keyed to the
                          // backend's original suggestion position even after we reorder for
                          // display (see the two call sites below that use __originalIndex).
                          const actionableItems = Array.isArray(rawActionableItems)
                            ? rawActionableItems
                                .map((s, i) => [s, i])
                                .sort((a, b) => {
                                  const sa = a[0]?.scoring?.priority_score;
                                  const sb = b[0]?.scoring?.priority_score;
                                  if (sa == null && sb == null) return a[1] - b[1];
                                  if (sa == null) return 1;
                                  if (sb == null) return -1;
                                  return sb - sa || a[1] - b[1];
                                })
                                .map(([s, i]) => (s && typeof s === 'object' ? { ...s, __originalIndex: i } : s))
                            : rawActionableItems;

                          // Analysis prose: Google Ads nests it under `analysis`, DV360 keeps
                          // campaign_analysis at the top level and adds pacing / optimization
                          // summaries that were never shown.
                          const analysisText = resolveOptimizationAnalysisText(details);

                          // Where the user actually applies an advisory recommendation. The
                          // note used to say "in Google Ads" on every platform, which is the
                          // wrong instruction for a DV360 line-item change.
                          const advisoryPlatformLabel = ADVISORY_PLATFORM_LABELS[campaign?.destination]
                            || 'the platform';

                          const hasActionableItems = Array.isArray(actionableItems) && actionableItems.length > 0;

                          // The Google Ads launchers nest their answer under
                          // details.analysis; DV360's sits at the top level. The table needs
                          // the same object the findings renderer reads, for the insertion
                          // order names it resolves ids against.
                          const focusedTableDetails = (details && typeof details === 'object'
                            && details.analysis && typeof details.analysis === 'object')
                            ? details.analysis
                            : details;

                          // A diagnostic run emits no recommendations list at all, so the
                          // feed printed "No changes recommended" under a run whose entire
                          // job is to say what to change. Its blockers ARE the work — each
                          // names an insertion order, the line items it covers and the one
                          // fix that clears it — so they fill the same table.
                          const diagnosticRows = focusedKind === 'diagnostic'
                            ? blockingIssueRows(focusedTableDetails).map((row, i) => ({
                              ...row, __originalIndex: i,
                            }))
                            : [];
                          const tableItems = diagnosticRows.length ? diagnosticRows : actionableItems;

                          // A focused run's rows render as a table instead of cards: every
                          // row answers the same six questions, so they line up in columns,
                          // and one row can carry the several line items a change applies to
                          // instead of repeating itself once per line. The cards did neither
                          // — on DV360 they never even named the insertion order, so a run
                          // covering seven IOs gave no way to tell which goal each served.
                          const isAudit = message.action === 'campaign-audit';
                          const isFocusedTable = (isAudit || FOCUSED_TABLE_KINDS.has(focusedKind))
                            && Array.isArray(tableItems) && tableItems.length > 0;

                          // A client-steered result takes this branch even with no suggestion
                          // cards. Its whole point is diagnosis-first: a campaign whose line items
                          // are all blocked legitimately yields zero recommendations, and that is
                          // precisely when the user most needs the Cause section. Gating the
                          // layout on the cards existing meant that run fell through to
                          // "No optimization suggestions available." over a full diagnosis.
                          return hasActionableItems || isClientResult || focusedKind ? (
                            <div className="optimization-suggestions">
                              {/* Campaign Analysis Summary - support both string and object formats.
                                  Suppressed for a client layout: resolveOptimizationAnalysisText
                                  joins campaign_analysis.overall_performance + pacing_summary +
                                  optimization_summary, and the client sections below render all
                                  three in the places the prompt's narrative puts them. Leaving both
                                  in printed every paragraph twice on the one screen this layout
                                  exists to organise. */}
                              {!isClientResult && !focusedKind && analysisText && (
                                <div style={{
                                  padding: '15px',
                                  backgroundColor: '#e7f3ff',
                                  border: '1px solid #b3d9ff',
                                  borderRadius: '6px',
                                  marginBottom: '20px'
                                }}>
                                  <h5 style={{ marginTop: 0, marginBottom: '10px', color: '#0056b3' }}>📊 Campaign Analysis</h5>
                                  <p style={{ margin: '0 0 10px 0', fontSize: '0.9em', lineHeight: '1.5' }}>
                                    {analysisText}
                                  </p>
                                  {/* Key Insights - only for Google Ads Search format */}
                                  {analysisData?.campaign_analysis?.key_insights &&
                                   analysisData.campaign_analysis.key_insights.length > 0 && (
                                    <div style={{ marginTop: '12px' }}>
                                      <strong style={{ fontSize: '0.85em', color: '#333' }}>Key Insights:</strong>
                                      <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                                        {analysisData.campaign_analysis.key_insights.map((insight, idx) => (
                                          <li key={idx} style={{ fontSize: '0.85em', marginBottom: '5px', color: '#555' }}>
                                            {insight.insight}
                                            {insight.severity && (
                                              <span style={{
                                                marginLeft: '8px',
                                                padding: '1px 6px',
                                                fontSize: '0.75em',
                                                fontWeight: '600',
                                                borderRadius: '3px',
                                                backgroundColor: insight.severity === 'HIGH' ? '#f8d7da' :
                                                               insight.severity === 'MEDIUM' ? '#fff3cd' : '#d4edda',
                                                color: insight.severity === 'HIGH' ? '#721c24' :
                                                      insight.severity === 'MEDIUM' ? '#856404' : '#155724'
                                              }}>
                                                {insight.severity}
                                              </span>
                                            )}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Older focused runs open on their recommendations, with no findings
                                  section above them. Diagnosis, pacing figures and per-line
                                  performance reads all landed here as a column of cards that
                                  the reader had to scroll past to reach anything actionable —
                                  and every fact they carried is either in the table below (the
                                  insertion order, the line items, the current value) or in the
                                  run's own raw result, which is one disclosure away. A
                                  client-steered result keeps its cause section: it is
                                  diagnosis-first by construction and has no table. */}
                              {!focusedKind && clientBreakdown && <clientBreakdown.Cause details={details} />}

                              {focusedKind && (message.json_response?.platform === 'ttd' || focusedTableDetails?.campaign_readout?.version === 1) && (
                                <FocusedFindingsSection kind={focusedKind} details={details} platform={message.json_response?.platform} />
                              )}

                              {/* Actionable Suggestions/Items */}
                              {focusedKind ? (
                                <h5 style={{ margin: '0 0 12px', color: '#111827' }}>Recommendations</h5>
                              ) : isClientResult ? (
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '2px' }}>
                                  <span style={{
                                    background: '#111827', color: '#fff', width: '20px', height: '20px',
                                    borderRadius: '50%', display: 'inline-flex', alignItems: 'center',
                                    justifyContent: 'center', fontSize: '0.7em', fontWeight: 700, flexShrink: 0,
                                  }}>2</span>
                                  <h5 style={{ margin: '0 0 12px', color: '#111827' }}>Recommendations</h5>
                                </div>
                              ) : (
                                <h5 style={{ marginBottom: '12px', color: '#333' }}>💡 Actionable Recommendations</h5>
                              )}
                              {(isClientResult || focusedKind) && !hasActionableItems && !isFocusedTable && (
                                <p style={{ fontSize: '0.85em', color: '#6b7280', margin: '0 0 8px' }}>
                                  {focusedKind
                                    ? emptyRunExplanation(focusedKind, focusedTableDetails)
                                    : 'No changes recommended — see the blockers above.'}
                                </p>
                              )}
                              {/* A focused run reads as a comparison — what is changing, where,
                                  and can I click it — which one-card-per-recommendation does
                                  not support. Apply is not reimplemented: renderAction hands
                                  back this component's own button, so the applying/applied
                                  state stays keyed where it always was. */}
                              {isFocusedTable && (
                                <FocusedRecommendationsTable
                                  items={tableItems}
                                  details={focusedTableDetails}
                                  platform={message.json_response?.platform}
                                  kind={isAudit ? 'audit' : focusedKind}
                                  campaignLabel={campaign?.name}
                                  /* The measured effect of the rows already applied.
                                     Null when nothing on this run was applied — a
                                     column of dashes is worse than no column. */
                                  impactFor={
                                    appliedImpacts && appliedImpacts[message.message_id]
                                      ? (applyIndex) => appliedImpacts[message.message_id][applyIndex] || null
                                      : null
                                  }
                                  renderAction={(suggestion, applyIndex) => {
                                    const isAdvisory = suggestion.auto_applicable === false
                                      || suggestion.advisory === true;
                                    if (isAdvisory || !onApplyChanges) {
                                      return (
                                        <span style={{ fontSize: '0.95em', color: '#7c3aed' }}>
                                          {isAudit ? 'Advisory — review required' : `Apply this manually in ${advisoryPlatformLabel}`}
                                          {/* One sentence. These reasons used to run to a
                                              paragraph explaining the executor's internals,
                                              in a cell beside the Apply buttons. */}
                                          {firstSentence(suggestion.advisory_reason) && (
                                            <span style={{ display: 'block', marginTop: '4px', color: '#6b7280' }}>
                                              {isAudit ? suggestion.advisory_reason : firstSentence(suggestion.advisory_reason)}
                                            </span>
                                          )}
                                        </span>
                                      );
                                    }
                                    const buttonId = `${message.message_id}-${applyIndex}`;
                                    const application = isSuggestionApplied
                                      ? isSuggestionApplied(message.message_id, applyIndex)
                                      : null;
                                    const isApplied = Boolean(application);
                                    const isCurrentlyApplying = applyingChanges === buttonId;
                                    return (
                                      <button
                                        className="btn btn-primary"
                                        style={{
                                          padding: '6px 12px', fontSize: '0.95em',
                                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                          textAlign: 'center', gap: '6px',
                                          backgroundColor: isApplied ? '#198754' : undefined,
                                          borderColor: isApplied ? '#198754' : undefined,
                                          color: isApplied ? '#fff' : undefined,
                                          opacity: 1,
                                          cursor: isApplied ? 'not-allowed' : 'pointer',
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (!isApplied) onApplyChanges(message, suggestion, applyIndex);
                                        }}
                                        disabled={applyingChanges !== null || isApplied}
                                      >
                                        {isCurrentlyApplying ? (
                                          <><FontAwesomeIcon icon={faSpinner} spin />Applying...</>
                                        ) : isApplied ? (
                                          <><FontAwesomeIcon icon={faCheckCircle} />{appliedLabel(application)}</>
                                        ) : (
                                          <><FontAwesomeIcon icon={faCheckCircle} />Apply Change</>
                                        )}
                                      </button>
                                    );
                                  }}
                                />
                              )}
                              {(isFocusedTable ? [] : (hasActionableItems ? actionableItems : [])).map((suggestion, index) => {
                                // Identity index for apply/applied-state: the suggestion's
                                // ORIGINAL backend position, preserved across display sorting.
                                // Falls back to the display index for non-scored/non-sorted lists.
                                const applyIndex = suggestion && suggestion.__originalIndex != null
                                  ? suggestion.__originalIndex
                                  : index;
                                // Create unique button ID using the identity index to avoid duplicates
                                const buttonId = `${message.message_id}-${applyIndex}`;

                                // --- Redesigned card: friendly labels + plain language ---
                                const ACTION_META = {
                                  keyword_targeting:         { icon: '🔑', label: 'Add keywords', cat: 'Keywords' },
                                  negative_keyword_targeting:{ icon: '🚫', label: 'Add negative keywords', cat: 'Negative keywords' },
                                  keyword_pause:             { icon: '⏸️', label: 'Pause keywords', cat: 'Keywords' },
                                  ad_copy:                   { icon: '✍️', label: 'Improve ad copy', cat: 'Creative' },
                                  ad_extension:              { icon: '🔗', label: 'Add ad extensions', cat: 'Extensions' },
                                  budget:                    { icon: '💰', label: 'Adjust budget', cat: 'Budget' },
                                  budget_reallocation:       { icon: '🔄', label: 'Reallocate budget', cat: 'Budget' },
                                  bidding_strategy_target:   { icon: '🎯', label: 'Tune bidding target', cat: 'Bidding' },
                                  bid:                       { icon: '💵', label: 'Adjust bid', cat: 'Bidding' },
                                  status:                    { icon: '⚡', label: 'Change status', cat: 'Status' },
                                  geography_targeting:       { icon: '📍', label: 'Refine locations', cat: 'Targeting' },
                                  ad_schedule:               { icon: '🗓️', label: 'Adjust ad schedule', cat: 'Targeting' },
                                  device_bid_adjustment:     { icon: '📱', label: 'Device bid adjustment', cat: 'Bidding' },
                                  audience_targeting:        { icon: '👥', label: 'Audience targeting', cat: 'Targeting' },
                                  landing_page:              { icon: '🌐', label: 'Fix landing page', cat: 'Landing page' },
                                  campaign_spend_limit:      { icon: '💰', label: 'Set spend limit', cat: 'Budget' },
                                  // Facebook holds its budget at exactly one level, so the two
                                  // levers are distinct actions, not one with a target field
                                  // (fb_budget_structure.py).
                                  daily_budget:              { icon: '💰', label: 'Adjust ad set daily budget', cat: 'Budget' },
                                  campaign_daily_budget:     { icon: '💰', label: 'Adjust campaign daily budget', cat: 'Budget' },
                                  // DV360's pacing vocabulary (campaign_optimizer/reasoning.py).
                                  ADJUST_DAILY_BUDGET:       { icon: '💰', label: 'Adjust daily budget', cat: 'Pacing' },
                                  ADD_FREQUENCY_CAP:         { icon: '🔁', label: 'Add frequency cap', cat: 'Pacing' },
                                  ADJUST_FREQUENCY_CAP:      { icon: '🔁', label: 'Adjust frequency cap', cat: 'Pacing' },
                                  ADD_AUDIENCE:              { icon: '👥', label: 'Add audience', cat: 'Targeting' },
                                  // The focused pacing launcher's vocabulary
                                  // (dv360_pacing_optimizer_launcher.py).
                                  ADJUST_DAILY_PACE:         { icon: '⏩', label: 'Adjust daily pace', cat: 'Pacing' },
                                  ADJUST_IO_DAILY_PACE:      { icon: '⏩', label: 'Adjust IO daily pace', cat: 'Pacing' },
                                  ADJUST_PACING_TYPE:        { icon: '⚙️', label: 'Adjust pacing type', cat: 'Pacing' },
                                  EXPAND_TARGETING:          { icon: '📈', label: 'Expand targeting', cat: 'Targeting' },
                                  REACTIVATE_LINE_ITEM:      { icon: '▶️', label: 'Reactivate line item', cat: 'Status' },
                                  // Campaign audit (campaign_audit/suggestions.py). PAUSE_*
                                  // are the audit's only one-click repairs besides the
                                  // allowlist; everything else it raises is advisory.
                                  PAUSE_LINE_ITEM:           { icon: '⏸️', label: 'Pause line item', cat: 'Status' },
                                  PAUSE_CAMPAIGN:            { icon: '⏸️', label: 'Pause campaign', cat: 'Status' },
                                  APPLY_ALLOWLIST:           { icon: '🛡️', label: 'Apply inventory allowlist', cat: 'Inventory' },
                                  AUDIT_FINDING:             { icon: '🔍', label: 'Audit finding', cat: 'Campaign audit' },
                                  // The focused performance launcher's levers
                                  // (dv360_performance_optimizer_launcher.py, via `lever`).
                                  BID:                       { icon: '💵', label: 'Adjust bid', cat: 'Bidding' },
                                  KEYWORD_EXPANSION:         { icon: '🔑', label: 'Expand keywords', cat: 'Targeting' },
                                  DOMAIN_EXPANSION:          { icon: '🌐', label: 'Expand domains', cat: 'Targeting' },
                                  NEW_AUDIENCE:              { icon: '👥', label: 'Add audience', cat: 'Targeting' },
                                  NEW_PMP_DEAL:              { icon: '🤝', label: 'Add PMP deal', cat: 'Inventory' },
                                  INVENTORY_EXCLUSION:       { icon: '🚫', label: 'Exclude inventory', cat: 'Inventory' },
                                  CREATIVE:                  { icon: '🎨', label: 'Creative change', cat: 'Creative' },
                                };
                                const at = suggestion.action_type;
                                const meta = ACTION_META[at] || {
                                  icon: '💡',
                                  label: at ? at.replace(/_/g, ' ') : 'Optimization',
                                  cat: suggestion.category || 'Optimization'
                                };
                                const plainSummary = suggestion.plain_summary || suggestion.description ||
                                                     suggestion.title || meta.label;
                                // Only Google Ads suggestions set auto_applicable; when the field is
                                // absent (FB/Reddit/MadHive) keep the historical always-applyable behavior.
                                // `advisory` is set by the DV360 resolver, whose action vocabulary the
                                // edit builder cannot turn into an edit.
                                // PMax audience-signal recommendations are inference-only (no measurable
                                // counterfactual) and are not offered as one-click applies — surface them
                                // as advisory so the user reviews and applies them by hand. (search_theme
                                // stays one-click applyable.)
                                const isPmaxAdvisoryAction =
                                  String(message.json_response?.platform || '').includes('pmax') &&
                                  suggestion.action_type === 'audience_signal';
                                const isAdvisory = suggestion.auto_applicable === false || suggestion.advisory === true || isPmaxAdvisoryAction;
                                const targetLabel = (suggestion.target_type && suggestion.target_id)
                                  ? `${suggestion.target_type.replace(/_/g, ' ')} ${suggestion.target_id}`
                                  : (suggestion.entity_type && suggestion.entity_name)
                                    ? `${suggestion.entity_type} ${suggestion.entity_name}`
                                    : null;
                                const chip = (bg, fg) => ({
                                  padding: '2px 8px', fontSize: '0.7em', fontWeight: 600,
                                  borderRadius: '10px', backgroundColor: bg, color: fg
                                });
                                const prio = { high: { bg: '#fee', fg: '#c00' }, medium: { bg: '#ffeaa7', fg: '#d63031' }, low: { bg: '#dfe6e9', fg: '#636e72' } }[suggestion.priority];
                                const hasDetails = (suggestion.current_value && suggestion.recommended_value) ||
                                  (suggestion.suggested_values && Object.keys(suggestion.suggested_values).length > 0) ||
                                  (suggestion.target_entities && suggestion.target_entities.length > 0);

                                // Scoring block (Google Ads optimizer): expected lift range,
                                // confidence, and a composite priority score. All optional —
                                // suggestions from platforms/older results that don't emit it
                                // simply render without this strip.
                                const sc = suggestion.scoring || null;
                                const lift = sc && sc.expected_lift ? sc.expected_lift : null;
                                const liftLabel = (() => {
                                  if (!lift) return null;
                                  const { range_low_pct: lo, range_high_pct: hi, point_pct: pt, direction, metric } = lift;
                                  const arrow = direction === 'decrease' ? '↓' : '↑';
                                  let mag = null;
                                  if (lo != null && hi != null && Number(lo) !== Number(hi)) {
                                    mag = `${Math.abs(Number(lo))}–${Math.abs(Number(hi))}%`;
                                  } else if (pt != null) {
                                    mag = `${Math.abs(Number(pt))}%`;
                                  }
                                  if (!mag) return null;
                                  return `${arrow} ${mag}${metric ? ' ' + String(metric).replace(/_/g, ' ') : ''}`;
                                })();
                                // 0–100 → color tier (green ≥70, amber ≥40, grey below).
                                const scoreColor = (v) => v == null ? { bg: '#f1f3f5', fg: '#636e72' }
                                  : v >= 70 ? { bg: '#e8f5e9', fg: '#2e7d32' }
                                  : v >= 40 ? { bg: '#fff3cd', fg: '#856404' }
                                  : { bg: '#f1f3f5', fg: '#636e72' };
                                const confTier = (v) => v == null ? '' : v >= 70 ? 'High' : v >= 40 ? 'Medium' : 'Low';

                                return (
                                  <div key={index} className="suggestion-card" style={{
                                    padding: '15px',
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #dee2e6',
                                    borderLeft: `4px solid ${isAdvisory ? '#7c3aed' : '#2e7d32'}`,
                                    borderRadius: '6px',
                                    marginBottom: '15px'
                                  }}>
                                    {/* Lead row: icon + plain-language summary + priority */}
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                      <div style={{ fontSize: '1.3em', lineHeight: 1.2 }}>{meta.icon}</div>
                                      <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: '1em', fontWeight: 600, color: '#1a1a1a', lineHeight: 1.4 }}>
                                          {plainSummary}
                                        </div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px', alignItems: 'center' }}>
                                          <span style={chip('#eef2ff', '#4338ca')}>{meta.cat}</span>
                                          {/* The ACTION the recommendation asks for. When the card's
                                              heading is a line-item name (pacing cards), this chip is
                                              the only place the action appears at all. */}
                                          {at && meta.label && meta.label !== meta.cat && (
                                            <span style={chip('#e7f5ff', '#1971c2')}>{meta.label}</span>
                                          )}
                                          {targetLabel && <span style={chip('#f1f3f5', '#495057')}>{targetLabel}</span>}
                                          <span style={chip(isAdvisory ? '#f3e8ff' : '#e8f5e9', isAdvisory ? '#7c3aed' : '#2e7d32')}>
                                            {isAdvisory ? 'ADVISORY' : 'ONE-CLICK'}
                                          </span>
                                        </div>
                                      </div>
                                      {prio && (
                                        <span style={{ ...chip(prio.bg, prio.fg), textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                                          {suggestion.priority}
                                        </span>
                                      )}
                                    </div>

                                    {/* Scoring strip: expected lift range, confidence, priority score.
                                        Tooltips carry the basis/rationale so the honest caveats
                                        (grounded vs. inferred, thin data) are one hover away. */}
                                    {sc && (sc.priority_score != null || liftLabel || sc.confidence != null) && (
                                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px', alignItems: 'center' }}>
                                        {sc.priority_score != null && (
                                          <span
                                            style={{ ...chip(scoreColor(sc.priority_score).bg, scoreColor(sc.priority_score).fg) }}
                                            title="Composite score = expected value × confidence × reversibility. Higher means do it first."
                                          >
                                            ★ Priority {sc.priority_score}/100
                                          </span>
                                        )}
                                        {liftLabel && (
                                          <span
                                            style={{ ...chip('#e7f5ff', '#1971c2') }}
                                            title={lift.basis || 'Estimated lift'}
                                          >
                                            Est. lift {liftLabel}
                                          </span>
                                        )}
                                        {sc.confidence != null && (
                                          <span
                                            style={{ ...chip(scoreColor(sc.confidence).bg, scoreColor(sc.confidence).fg) }}
                                            title={sc.confidence_rationale || 'Reliability of the lift estimate (data volume + how directly it is grounded).'}
                                          >
                                            Confidence {confTier(sc.confidence)} ({sc.confidence}/100)
                                          </span>
                                        )}
                                      </div>
                                    )}

                                    {/* Target Entities - for Facebook/Reddit */}
                                    {suggestion.target_entities && suggestion.target_entities.length > 0 && (
                                      <div style={{
                                        marginTop: '10px',
                                        padding: '10px 12px',
                                        backgroundColor: '#fff',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '4px'
                                      }}>
                                        <div style={{ fontSize: '0.75em', color: '#666', marginBottom: '6px', fontWeight: '600' }}>
                                          TARGET ENTITIES
                                        </div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                          {suggestion.target_entities.map((entity, idx) => (
                                            <code key={idx} style={{
                                              backgroundColor: '#f5f5f5',
                                              padding: '4px 8px',
                                              borderRadius: '3px',
                                              fontSize: '0.85em',
                                              color: '#333',
                                              fontFamily: 'monospace'
                                            }}>
                                              {entity}
                                            </code>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {/* Current vs Recommended Values - collapsed into a
                                        "Technical details" disclosure to keep the card plain-language. */}
                                    {suggestion.current_value && suggestion.recommended_value && (
                                     <details style={{ marginTop: '10px' }}>
                                      <summary style={{ cursor: 'pointer', fontSize: '0.78em', color: '#666', fontWeight: 600 }}>
                                        Technical details (current → recommended)
                                      </summary>
                                      <div style={{
                                        marginTop: '8px',
                                        padding: '12px',
                                        backgroundColor: '#fff',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '4px',
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr',
                                        gap: '12px'
                                      }}>
                                        <div>
                                          <div style={{ fontSize: '0.75em', color: '#666', marginBottom: '4px', fontWeight: '600' }}>
                                            CURRENT VALUE
                                          </div>
                                          <SuggestionValue value={suggestion.current_value} tone="current" />
                                        </div>
                                        <div>
                                          <div style={{ fontSize: '0.75em', color: '#666', marginBottom: '4px', fontWeight: '600' }}>
                                            RECOMMENDED VALUE
                                          </div>
                                          <SuggestionValue
                                            value={suggestion.recommended_value}
                                            applyValue={suggestion.apply?.value}
                                            additions={keywordAdditions(suggestion)}
                                            tone="recommended" />
                                        </div>
                                      </div>
                                     </details>
                                    )}

                                    {/* Suggested keywords with real Keyword Planner strength
                                        (Google Ads keyword_targeting). Rendered as a table when
                                        recommended_value is an array of keyword objects. */}
                                    {suggestion.action_type === 'keyword_targeting' &&
                                     Array.isArray(suggestion.recommended_value) &&
                                     suggestion.recommended_value.some(k => k && typeof k === 'object' && k.keyword) && (
                                      <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                                        <div style={{ fontSize: '0.75em', color: '#666', marginBottom: '8px', fontWeight: '600' }}>
                                          SUGGESTED KEYWORDS &amp; STRENGTH
                                        </div>
                                        <div style={{ overflowX: 'auto' }}>
                                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82em' }}>
                                            <thead>
                                              <tr style={{ borderBottom: '1px solid #dee2e6', color: '#495057', textAlign: 'left' }}>
                                                <th style={{ padding: '6px 8px' }}>Keyword</th>
                                                <th style={{ padding: '6px 8px' }}>Match</th>
                                                <th style={{ padding: '6px 8px', textAlign: 'right' }}>Avg/mo searches</th>
                                                <th style={{ padding: '6px 8px' }}>Competition</th>
                                                <th style={{ padding: '6px 8px', textAlign: 'right' }}>Top-of-page bid</th>
                                                <th style={{ padding: '6px 8px' }}>Strength</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {suggestion.recommended_value.map((k, ki) => {
                                                const kw = (k && typeof k === 'object') ? k : { keyword: k };
                                                const isNewKeyword = keywordAdditions(suggestion)?.has(keywordKey(kw.keyword));
                                                const strength = kw.strength || 'NO_DATA';
                                                const strengthColors = {
                                                  STRONG: { bg: '#e8f5e9', fg: '#2e7d32' },
                                                  MODERATE: { bg: '#e3f2fd', fg: '#1565c0' },
                                                  WEAK: { bg: '#fff3e0', fg: '#e65100' },
                                                  NO_VOLUME: { bg: '#f5f5f5', fg: '#757575' },
                                                  NO_DATA: { bg: '#f5f5f5', fg: '#9e9e9e' }
                                                };
                                                const sc = strengthColors[strength] || strengthColors.NO_DATA;
                                                const hasBid = kw.low_top_of_page_bid != null || kw.high_top_of_page_bid != null;
                                                const bidLabel = hasBid
                                                  ? `${formatOptimizerCurrency(kw.low_top_of_page_bid ?? kw.high_top_of_page_bid, currencyCode)}–${formatOptimizerCurrency(kw.high_top_of_page_bid ?? kw.low_top_of_page_bid, currencyCode)}`
                                                  : '—';
                                                return (
                                                  <tr key={ki} style={{ borderBottom: '1px solid #f1f3f5' }}>
                                                    <td style={{ padding: '6px 8px', color: '#212529', ...(isNewKeyword ? newKeywordStyle : {}) }}>{kw.keyword}{isNewKeyword && <span> · New</span>}</td>
                                                    <td style={{ padding: '6px 8px', color: '#495057' }}>{kw.match_type || '—'}</td>
                                                    <td style={{ padding: '6px 8px', textAlign: 'right', color: '#212529' }}>
                                                      {kw.avg_monthly_searches != null ? Number(kw.avg_monthly_searches).toLocaleString() : '—'}
                                                    </td>
                                                    <td style={{ padding: '6px 8px', color: '#495057' }}>{kw.competition || '—'}</td>
                                                    <td style={{ padding: '6px 8px', textAlign: 'right', color: '#212529' }}>{bidLabel}</td>
                                                    <td style={{ padding: '6px 8px' }}>
                                                      <span style={{ backgroundColor: sc.bg, color: sc.fg, padding: '2px 8px', borderRadius: '10px', fontSize: '0.9em', fontWeight: 600 }}>
                                                        {strength.replace(/_/g, ' ')}
                                                      </span>
                                                    </td>
                                                  </tr>
                                                );
                                              })}
                                            </tbody>
                                          </table>
                                        </div>
                                        <div style={{ fontSize: '0.72em', color: '#999', marginTop: '6px' }}>
                                          Search volume &amp; bids from Google Keyword Planner, scoped to this campaign's location &amp; language targeting.
                                        </div>
                                      </div>
                                    )}

                                    {/* Suggested Values - for Facebook/Reddit */}
                                    {suggestion.suggested_values && Object.keys(suggestion.suggested_values).length > 0 && (
                                      <div style={{
                                        marginTop: '12px',
                                        padding: '12px',
                                        backgroundColor: '#fff',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '4px'
                                      }}>
                                        <div style={{ fontSize: '0.75em', color: '#666', marginBottom: '8px', fontWeight: '600' }}>
                                          SUGGESTED VALUES
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                          {Object.entries(suggestion.suggested_values).map(([key, value]) => {
                                            // Format value - if key contains 'budget' or 'bid', format as currency
                                            let formattedValue;
                                            const isFacebookPlatform = message.json_response?.platform === 'fb' || message.json_response?.platform === 'facebook';
                                            if (typeof value === 'number' && key === 'bid_amount' && isFacebookPlatform) {
                                              // Facebook bid_amount is always in cents (40 = $0.40)
                                              formattedValue = `$${(value / 100).toFixed(2)}`;
                                            } else if (typeof value === 'number' && (key.toLowerCase().includes('budget') || key.toLowerCase().includes('bid') || key.toLowerCase().includes('amount'))) {
                                              // Other monetary values: use heuristic (>= 100 = cents, < 100 = dollars)
                                              // then label in the account's real currency (not a hardcoded "$").
                                              const dollars = value >= 100 ? value / 100 : value;
                                              formattedValue = formatOptimizerCurrency(dollars, currencyCode);
                                            } else if (typeof value === 'object') {
                                              formattedValue = JSON.stringify(value);
                                            } else {
                                              formattedValue = String(value);
                                            }

                                            return (
                                              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <code style={{
                                                  backgroundColor: '#f5f5f5',
                                                  padding: '4px 8px',
                                                  borderRadius: '3px',
                                                  fontSize: '0.85em',
                                                  color: '#666',
                                                  fontFamily: 'monospace',
                                                  minWidth: '120px'
                                                }}>
                                                  {key}
                                                </code>
                                                <code style={{
                                                  backgroundColor: '#e8f5e9',
                                                  padding: '4px 8px',
                                                  borderRadius: '3px',
                                                  fontSize: '0.9em',
                                                  color: '#2e7d32',
                                                  fontWeight: '600',
                                                  fontFamily: 'monospace'
                                                }}>
                                                  {formattedValue}
                                                </code>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    )}

                                    {/* Reasoning - supports both 'reasoning' (Facebook) and 'rationale' (Reddit) */}
                                    {(suggestion.reasoning || suggestion.rationale) && (
                                      <div style={{ marginTop: '12px' }}>
                                        <div style={{ fontSize: '0.8em', color: '#666', marginBottom: '4px', fontWeight: '600' }}>
                                          💡 REASONING
                                        </div>
                                        <p style={{ margin: '0', fontSize: '0.9em', color: '#333', lineHeight: '1.5' }}>
                                          {suggestion.reasoning || suggestion.rationale}
                                        </p>
                                      </div>
                                    )}

                                    {/* Expected Impact */}
                                    {suggestion.expected_impact && (
                                      <div style={{ marginTop: '12px' }}>
                                        <div style={{ fontSize: '0.8em', color: '#666', marginBottom: '4px', fontWeight: '600' }}>
                                          📈 EXPECTED IMPACT
                                        </div>
                                        <p style={{ margin: '0', fontSize: '0.9em', color: '#333', lineHeight: '1.5' }}>
                                          {suggestion.expected_impact}
                                        </p>
                                      </div>
                                    )}

                                    {/* DV360 recommendations carry the steps to take and the line
                                        items they affect — the actionable half of an advisory
                                        recommendation, and previously not rendered at all. */}
                                    {Array.isArray(suggestion.implementation_steps) && suggestion.implementation_steps.length > 0 && (
                                      <div style={{ marginTop: '12px' }}>
                                        <div style={{ fontSize: '0.8em', color: '#666', marginBottom: '4px', fontWeight: '600' }}>
                                          🧭 HOW TO APPLY
                                        </div>
                                        <ol style={{ margin: 0, paddingLeft: '20px', fontSize: '0.9em', color: '#333', lineHeight: '1.6' }}>
                                          {suggestion.implementation_steps.map((step, si) => (
                                            <li key={si}>{typeof step === 'string' ? step : JSON.stringify(step)}</li>
                                          ))}
                                        </ol>
                                      </div>
                                    )}

                                    {Array.isArray(suggestion.affected_line_items) && suggestion.affected_line_items.length > 0 && (
                                      <div style={{ marginTop: '12px' }}>
                                        <div style={{ fontSize: '0.8em', color: '#666', marginBottom: '4px', fontWeight: '600' }}>
                                          🎯 AFFECTED LINE ITEMS
                                        </div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                          {suggestion.affected_line_items.map((li, li_i) => (
                                            <code key={li_i} style={{
                                              backgroundColor: '#f5f5f5', padding: '3px 8px', borderRadius: '3px',
                                              fontSize: '0.85em', color: '#555', fontFamily: 'monospace',
                                            }}>{typeof li === 'string' || typeof li === 'number' ? li : JSON.stringify(li)}</code>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {/* Advisory note — this optimization type is not one-click
                                        applyable, so guide the user to act on it manually. */}
                                    {isAdvisory && (
                                      <div style={{ marginTop: '12px', fontSize: '0.82em', color: '#7c3aed', display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                                        <span>ℹ️</span>
                                        <span>
                                          {suggestion.advisory_reason
                                            ? `Advisory — ${suggestion.advisory_reason}.`
                                            : `Advisory — review and apply this manually in ${advisoryPlatformLabel}.`}
                                        </span>
                                      </div>
                                    )}

                                    {/* Apply Changes Button — only for one-click-applyable suggestions */}
                                    {onApplyChanges && !isAdvisory && (() => {
                                      const application = isSuggestionApplied
                                        ? isSuggestionApplied(message.message_id, applyIndex)
                                        : null;
                                      const isApplied = Boolean(application);
                                      const isCurrentlyApplying = applyingChanges === buttonId;

                                      return (
                                        <button
                                          className="btn btn-primary"
                                          style={{
                                            marginTop: '12px',
                                            padding: '8px 16px',
                                            fontSize: '0.85em',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            textAlign: 'center',
                                            gap: '6px',
                                            backgroundColor: isApplied ? '#198754' : undefined,
                                            borderColor: isApplied ? '#198754' : undefined,
                                            color: isApplied ? '#fff' : undefined,
                                            opacity: 1,
                                            cursor: isApplied ? 'not-allowed' : 'pointer'
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (!isApplied) {
                                              onApplyChanges(message, suggestion, applyIndex);
                                            }
                                          }}
                                          disabled={applyingChanges !== null || isApplied}
                                        >
                                          {isCurrentlyApplying ? (
                                            <>
                                              <FontAwesomeIcon icon={faSpinner} spin />
                                              Applying...
                                            </>
                                          ) : isApplied ? (
                                            <>
                                              <FontAwesomeIcon icon={faCheckCircle} />
                                              {appliedLabel(application)}
                                            </>
                                          ) : (
                                            <>
                                              <FontAwesomeIcon icon={faCheckCircle} />
                                              Apply Changes
                                            </>
                                          )}
                                        </button>
                                      );
                                    })()}
                                  </div>
                                );
                              })}

                              {focusedKind
                                ? <FocusedVerdictSection kind={focusedKind} details={details} platform={message.json_response?.platform} />
                                : (clientBreakdown && <clientBreakdown.Expansions details={details} />)}

                              {/* The optimizer's own closing summary. Rendered for a client layout
                                  so the three sections end where the model's narrative ends; the
                                  generic layout already surfaces its analysis prose at the top. */}
                              {isClientResult && details.optimization_summary && (
                                <div style={{
                                  padding: '12px 15px', background: '#f8f9ff', borderRadius: '6px',
                                  marginTop: '14px', fontSize: '0.85em', lineHeight: 1.5, color: '#374151',
                                }}>
                                  <strong style={{ display: 'block', marginBottom: '4px' }}>Summary</strong>
                                  {details.optimization_summary}
                                </div>
                              )}
                        </div>
                      ) : (() => {
                          // DV360 optimize results use a different shape than Google Ads / FB:
                          // recommendations, pacing_recommendations, campaign_analysis,
                          // optimization_summary and audience_expansion_recommendations live
                          // directly on `details` (there is no `actionable_items` /
                          // `analysis.actionable_suggestions`). Render them here so DV360 runs
                          // don't fall through to "No optimization suggestions available."
                          const ca = details.campaign_analysis || {};
                          const pacing = Array.isArray(details.pacing_recommendations) ? details.pacing_recommendations : [];
                          const recs = Array.isArray(details.recommendations) ? details.recommendations : [];
                          const audience = Array.isArray(details.audience_expansion_recommendations) ? details.audience_expansion_recommendations : [];
                          const keyIssues = Array.isArray(ca.key_issues) ? ca.key_issues : [];
                          const hasDv360Content = pacing.length || recs.length || audience.length || keyIssues.length
                            || ca.overall_performance || ca.pacing_summary || ca.overall_analysis || details.optimization_summary;

                          if (!hasDv360Content) {
                            return <p style={{ fontSize: '0.9em', color: '#666' }}>No optimization suggestions available.</p>;
                          }

                          const tone = (level) => {
                            const l = String(level || '').toLowerCase();
                            if (l === 'high' || l === 'overpacing') return { bg: '#fee', color: '#c00' };
                            if (l === 'medium' || l === 'underpacing') return { bg: '#fef3c7', color: '#b45309' };
                            if (l === 'low' || l === 'on_pace' || l === 'on pace') return { bg: '#E8FAF2', color: '#0F7B5F' };
                            return { bg: '#eef', color: '#3451b2' };
                          };
                          const badge = (level, text) => {
                            const t = tone(level);
                            return <span style={{ background: t.bg, color: t.color, padding: '2px 8px', borderRadius: '10px', fontSize: '0.72em', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{text}</span>;
                          };
                          const cardStyle = { border: '1px solid #e5e7eb', borderRadius: '6px', padding: '12px', marginTop: '8px', background: '#fff' };

                          return (
                            <div className="optimization-suggestions dv360-optimization">
                              {ca.overall_performance && (
                                <div style={{ padding: '15px', background: '#f8f9ff', borderRadius: '6px', marginBottom: '10px', fontSize: '0.9em', lineHeight: 1.5 }}>
                                  {ca.overall_performance}
                                </div>
                              )}

                              {ca.pacing_summary && (
                                <div style={{ padding: '12px 15px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '6px', marginBottom: '10px', fontSize: '0.88em', lineHeight: 1.5 }}>
                                  <strong style={{ display: 'block', marginBottom: '4px' }}>Pacing Summary</strong>
                                  {ca.pacing_summary}
                                </div>
                              )}

                              {keyIssues.length > 0 && (
                                <div style={{ marginBottom: '10px' }}>
                                  <h5 style={{ margin: '0 0 6px' }}>Key Issues</h5>
                                  {keyIssues.map((issue, i) => (
                                    <div key={i} style={cardStyle}>
                                      <div style={{ marginBottom: '4px' }}>{badge(issue.severity, issue.severity)}</div>
                                      <p style={{ margin: 0, fontSize: '0.88em' }}>{issue.issue}</p>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {pacing.length > 0 && (
                                <div style={{ marginBottom: '10px' }}>
                                  <h5 style={{ margin: '0 0 6px' }}>⏱️ Pacing Recommendations</h5>
                                  {pacing.map((p, i) => (
                                    <div key={i} style={cardStyle}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }}>
                                        <strong style={{ fontSize: '0.9em' }}>{(p.action_type ? String(p.action_type).replace(/_/g, ' ') : 'Pacing')} — {p.line_item_name || p.line_item_id}</strong>
                                        {p.pacing_status && badge(p.pacing_status, String(p.pacing_status).replace(/_/g, ' '))}
                                        {p.expected_impact && badge(p.expected_impact, `${p.expected_impact} impact`)}
                                      </div>
                                      {(p.current_value != null || p.recommended_value != null) && (
                                        <div style={{ fontFamily: 'monospace', fontSize: '0.82em', marginBottom: '6px', wordBreak: 'break-word' }}>
                                          {p.current_value != null && <span style={{ background: '#f3f4f6', padding: '1px 6px', borderRadius: '3px' }}>{p.current_value}</span>}
                                          {p.current_value != null && p.recommended_value != null && ' → '}
                                          {p.recommended_value != null && <span style={{ background: '#E8FAF2', color: '#0F7B5F', padding: '1px 6px', borderRadius: '3px', fontWeight: 600 }}>{p.recommended_value}</span>}
                                        </div>
                                      )}
                                      {p.rationale && <p style={{ margin: 0, fontSize: '0.85em', color: '#444' }}>{p.rationale}</p>}
                                    </div>
                                  ))}
                                </div>
                              )}

                              {recs.length > 0 && (
                                <div style={{ marginBottom: '10px' }}>
                                  <h5 style={{ margin: '0 0 6px' }}>Recommendations</h5>
                                  {recs.map((rec, i) => (
                                    <div key={i} style={cardStyle}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }}>
                                        <strong style={{ fontSize: '0.9em' }}>{rec.title}</strong>
                                        {rec.expected_impact && badge(rec.expected_impact, `${rec.expected_impact} impact`)}
                                      </div>
                                      {rec.description && <p style={{ margin: '0 0 6px', fontSize: '0.85em' }}>{rec.description}</p>}
                                      {Array.isArray(rec.implementation_steps) && rec.implementation_steps.length > 0 && (
                                        <ol style={{ margin: 0, paddingLeft: '18px', fontSize: '0.83em', color: '#444' }}>
                                          {rec.implementation_steps.map((step, si) => <li key={si}>{step}</li>)}
                                        </ol>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}

                              {audience.length > 0 && (
                                <div style={{ marginBottom: '10px' }}>
                                  <h5 style={{ margin: '0 0 6px' }}>🎯 Audience Expansion Recommendations</h5>
                                  {audience.map((a, i) => (
                                    <div key={i} style={cardStyle}>
                                      <strong style={{ fontSize: '0.88em' }}>Line Item: {a.line_item_id}</strong>
                                      {a.google_audience_to_expand && <div style={{ fontSize: '0.85em', marginTop: '4px' }}>Google audience: <strong>{a.google_audience_to_expand}</strong></div>}
                                      {a.first_party_audience_to_expand && <div style={{ fontSize: '0.85em', marginTop: '2px' }}>First-party audience: <strong>{a.first_party_audience_to_expand}</strong></div>}
                                    </div>
                                  ))}
                                </div>
                              )}

                              {details.optimization_summary && (
                                <div style={{ padding: '12px 15px', background: '#f8f9ff', borderRadius: '6px', fontSize: '0.88em', lineHeight: 1.5 }}>
                                  <strong style={{ display: 'block', marginBottom: '4px' }}>Optimization Summary</strong>
                                  {details.optimization_summary}
                                </div>
                              )}
                            </div>
                          );
                        })();
                        })()}

                      {/* Render reporting data visualization if available. Pass the account
                          currency (resolved from customer.currency_code) so money renders in
                          the real currency, not a hardcoded "$". */}
                      {message.json_response?.details?.reporting_data && (
                        <ReportingDataVisualization
                          reportingData={message.json_response.details.reporting_data}
                          currency={message.json_response?.analysis_data?.campaign_structure?.currency_code || null}
                        />
                      )}

                      {/* Render reporting data from analysis_data.performance_data (Google Ads Search) */}
                      {message.json_response?.analysis_data?.performance_data && (
                        <ReportingDataVisualization
                          reportingData={message.json_response.analysis_data.performance_data}
                          currency={message.json_response?.analysis_data?.campaign_structure?.currency_code || null}
                        />
                      )}
                    </div>
                  )}

                  {/* LLM summary for failed messages with no json_response */}
                  {isAiErrorSummaryEnabled && message.status === 'failed' && !hasResult && (
                    <div className="detail-section">
                      <div className="result-header">
                        <h4>What happened</h4>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          {!llmSummaries[message.message_id]?.loading && (
                            <button
                              className="download-json-button"
                              onClick={(e) => { e.stopPropagation(); summarizeMessage(message); }}
                              title="Summarize failure"
                            >
                              <FontAwesomeIcon icon={faExclamationTriangle} />
                              <span>{(llmSummaries[message.message_id]?.errors?.length || llmSummaries[message.message_id]?.warnings?.length || llmSummaries[message.message_id]?.infos?.length || message.llm_summary) ? 'Refresh summary' : 'Summarize'}</span>
                            </button>
                          )}
                        </div>
                      </div>
                      <CampaignIssueTable
                        summary={llmSummaries[message.message_id] || message.llm_summary}
                        errors={message.content ? [message.content] : []}
                        hideHighOperationCountWarnings={hideHighOperationCountWarnings}
                      />
                    </div>
                  )}

                  {showResultSection && (
                    <div className="detail-section">
                      <div className="result-header">
                        <h4>{resultSectionTitle}</h4>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          {canViewRawJson && (
                            <button
                              className="download-json-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                const jsonToDownload = message.message_type === 'gt-api'
                                  ? reorganizeGtApiJson(message.json_response)
                                  : message.json_response;
                                downloadJson(jsonToDownload, message.message_id, message);
                              }}
                              title="Download JSON"
                            >
                              <FontAwesomeIcon icon={faDownload} />
                              <span>Download JSON</span>
                            </button>
                          )}

                          {/* Download SDF button for YouTube DV360 edit-campaign with line item operations */}
                          {(message.action === 'edit-campaign' || message.message_type === 'edit-campaign') &&
                           campaign?.destination === 'youtube-dv360' &&
                           message.json_response?.uploaded_sdf_files?.length > 0 && (
                            <button
                              className="download-json-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                const sdfFile = message.json_response.uploaded_sdf_files[0];
                                downloadSdfFile(sdfFile.gcs_path, sdfFile.filename);
                              }}
                              title="Download SDF"
                            >
                              <FontAwesomeIcon icon={faDownload} />
                              <span>Download SDF</span>
                            </button>
                          )}
                          {/* Download Ad SDF button for YouTube DV360 assign-creatives with ad_sdf_gcs_path */}
                          {(message.action === 'assign-creatives' || message.message_type === 'assign-creatives') &&
                           message.json_response?.ad_sdf_gcs_path && (
                            <button
                              className="download-json-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                const adSdfPath = message.json_response.ad_sdf_gcs_path;
                                const adSdfFilename = adSdfPath.split('/').pop();
                                downloadSdfFile(adSdfPath, adSdfFilename);
                              }}
                              title="Download Ad SDF"
                            >
                              <FontAwesomeIcon icon={faDownload} />
                              <span>Download Ad SDF</span>
                            </button>
                          )}
                          {/* Download Source File button(s) for assign-creatives/upload-creatives/create-geofence-adgroups */}
                          {(() => {
                            const validFiles = downloadableSourceFiles;
                            if (validFiles.length === 0) return null;
                            if (validFiles.length === 1) {
                              return (
                                <button
                                  className="download-json-button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    downloadSourceFile(validFiles[0].gcsPath);
                                  }}
                                  title={`Download ${(validFiles[0].name || validFiles[0].gcsPath || 'Source File').split('/').pop()}`}
                                >
                                  <FontAwesomeIcon icon={faDownload} />
                                  <span>Download Source File</span>
                                </button>
                              );
                            }
                            // Multiple files — use a dropdown
                            return (
                              <div style={{ position: 'relative', display: 'inline-block' }}>
                                <button
                                  className="download-json-button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const menu = e.currentTarget.nextElementSibling;
                                    menu.style.display = menu.style.display === 'block' ? 'none' : 'block';
                                  }}
                                >
                                  <FontAwesomeIcon icon={faDownload} />
                                  <span>Source Files ({validFiles.length})</span>
                                </button>
                                <div style={{
                                  display: 'none', position: 'absolute', top: '100%', right: 0, zIndex: 100,
                                  background: '#fff', border: '1px solid #ddd', borderRadius: '6px',
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)', minWidth: '240px', maxWidth: '360px', marginTop: '4px',
                                  overflow: 'hidden',
                                }}>
                                  {validFiles.map((file, idx) => {
                                    const displayName = (file.name || file.gcsPath || `Source File ${idx + 1}`).split('/').pop();
                                    return (
                                    <div
                                      key={idx}
                                      style={{
                                        padding: '10px 14px', cursor: 'pointer', fontSize: '13px',
                                        borderBottom: idx < validFiles.length - 1 ? '1px solid #eee' : 'none',
                                        display: 'flex', alignItems: 'center', gap: '8px',
                                      }}
                                      onMouseEnter={(e) => e.currentTarget.style.background = '#f5f7ff'}
                                      onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        downloadSourceFile(file.gcsPath);
                                        e.currentTarget.parentElement.style.display = 'none';
                                      }}
                                      title={file.name || file.gcsPath}
                                    >
                                      <FontAwesomeIcon icon={faDownload} style={{ color: '#4a6cf7', fontSize: '12px', flexShrink: 0 }} />
                                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</span>
                                    </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })()}
                          {/* Summarize button — shown when errors/warnings exist, not loading/summarized, and feature enabled */}
                          {isAiErrorSummaryEnabled && (() => {
                            const { errors: btnErrors, warnings: btnWarnings } = getErrorsAndWarnings(message);
                            const btnSummaryData = llmSummaries[message.message_id];
                            const hasContent = message.status === 'failed'
                              ? !!message.json_response
                              : btnErrors.length > 0 || btnWarnings.length > 0;
                            if (!hasContent) return null;
                            if (btnSummaryData?.loading) return null;
                            return (
                              <button
                                className="download-json-button"
                                onClick={(e) => { e.stopPropagation(); summarizeMessage(message); }}
                                title="Summarize errors & warnings"
                              >
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                <span>{(llmSummaries[message.message_id]?.errors?.length || llmSummaries[message.message_id]?.warnings?.length || llmSummaries[message.message_id]?.infos?.length || message.llm_summary) ? 'Refresh summary' : 'Summarize'}</span>
                              </button>
                            );
                          })()}
                        </div>
                      </div>
                      {(() => {
                        const { errors, warnings, infos } = getErrorsAndWarnings(message);
                        return <CampaignIssueTable
                          summary={isAiErrorSummaryEnabled ? (llmSummaries[message.message_id] || message.llm_summary) : null}
                          errors={errors} warnings={warnings} infos={infos}
                          hideHighOperationCountWarnings={hideHighOperationCountWarnings}
                        />;
                      })()}
                      {canViewRawJson && (
                        <div className="result-content">
                          {/* Only true while the account has NOT opted in. Once
                              `enable-result-section` is on, everyone here sees
                              the JSON, so claiming otherwise would be a lie. */}
                          {!isResultSectionShared && (
                            <div style={{
                              display: 'flex', alignItems: 'center', gap: '8px',
                              padding: '8px 12px', marginBottom: '8px',
                              backgroundColor: '#fff8e1', border: '1px solid #ffe082',
                              borderRadius: '6px', color: '#8a6100', fontSize: '0.82em',
                            }}>
                              <FontAwesomeIcon icon={faLock} />
                              <span>
                                <strong>Super admin only.</strong> The raw response and its
                                JSON download are visible to you alone — everyone else on this
                                account sees this activity item without them.
                              </span>
                            </div>
                          )}
                          <pre>
                            {highlightErrors(
                              JSON.stringify(
                                message.message_type === 'gt-api'
                                  ? reorganizeGtApiJson(message.json_response)
                                  : message.json_response,
                                null,
                                2
                              )
                            )}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CampaignActivity;
